// Editor.jsx — il CUORE del modulo Fatturazione (fatt-v1, CONTRATTO-MVP §UI).
// Layout a due colonne (form + riepilogo sticky; una colonna sotto 768px).
// Livello BASE: cliente (select ricercabile + nuovo cliente inline), tipo
// documento (TD base in evidenza + gruppo Integrazioni/Autofatture TD16-TD29
// con avviso e ruoliInvertiti read-only), formato auto da cliente.isPa (banner
// FPA12 + CIG/CUP inline), data, serie + anteprima numero, griglia righe con
// datalist voci, pagamento a RATE MULTIPLE (TP02/MP05 default; con più rate
// condizioni TP01 automatiche + mini-form 'Genera piano rate…' su rate-core),
// riepilogo LIVE. Sezioni AVANZATE collassate con badge 'compilato'. Autosave
// debounce 2s + on-blur con PATCH/PUT awaited; BOZZA ON-DEMAND: il POST parte
// SOLO alla prima modifica reale (aprire 'Nuova fattura' non crea nulla);
// 409 ⇒ sospensione autosave + banner bloccante con le 2 azioni del piano;
// Valida ⇒ pannello problemi cliccabili (editor-core.mappaCampoSezione +
// focus [data-campo]); Anteprima XML in modale; Anteprima PDF di cortesia
// (pdf.js + jsPDF self-hosted caricato on-demand, nuova scheda bloburl);
// ClienteForm con autofill: VIES da P.IVA (solo campi vuoti), autocomplete
// indirizzo (/geo/suggest, sovrascrive su scelta esplicita), IndicePA per le
// PA, «Cerca SDI e PEC» (OpenAPI Company, A PAGAMENTO: bottone ESPLICITO, mai
// automatico, disabilitato se il servizio non è configurato, costo nel toast).
// Invia PRESENTE ma disabilitato (fase 2). Doppio submit protetto da
// ref SINCRONI; ESC/chiusura con modifiche non salvate ⇒ conferma.
//
// Regole gestionale (CLAUDE.md §3/§4): il server è l'unica verità — dopo OGNI
// salvataggio i numeri mostrati sono quelli del SERVER (l'anteprima calc-client
// copre solo lo stato dirty); ogni scrittura conferma o fallisce RUMOROSAMENTE
// (indicatore 'Salvato alle HH:MM' / banner rosso persistente, dati che
// RESTANO nel form); intenzioni piccole (PATCH testata / PUT righe), mai
// fotografie di stato estranee.

import React from 'react';
import { api } from './api.js';
import { anteprima } from './calc-client.js';
import { buildFatturaPdf } from './pdf.js';
import { generaRate, sommaRate } from './rate-core.js';
import {
  TIPI_DOCUMENTO, TD_BASE, NATURE, MODALITA_PAGAMENTO, CONDIZIONI_PAGAMENTO,
  TIPI_RITENUTA, CAUSALI_PAGAMENTO, TIPI_CASSA, TIPI_CESSIONE, ALIQUOTE_IVA,
  IVA_PRESETS, ivaPresetValue, ivaPresetParse,
  tdRuoliInvertiti, tdIntegrazione,
  buildRigaVuota, buildRataVuota, buildPagamentoVuoto, buildFatturaVuota, buildDocRifVuoto,
  mappaCampoSezione, sezioneCompilata, rigaAvanzataCompilata, SEZIONI_AVANZATE,
  validaClienteFormato, ibanValido, debounce, parseIntList, joinIntList,
  numOrNull, fmtEuro, fmtOra, oggiISO, nomeCliente,
} from './editor-core.js';

// ---------------------------------------------------------------------------
// serializzazione (intenzioni verso il server)
// ---------------------------------------------------------------------------
function serializeTestata(f) {
  return {
    formato: f.formato,
    tipoDocumento: f.tipoDocumento,
    divisa: f.divisa,
    dataEmissione: f.dataEmissione,
    serie: f.serie || '',
    art73: !!f.art73,
    clienteId: f.clienteId,
    datiGenerali: f.datiGenerali,
    pagamento: f.pagamento,
  };
}

function serializeRighe(righe) {
  return (righe || []).map((r) => {
    const o = Object.assign({}, r);
    delete o._key;
    return o;
  });
}

function serializeForCalc(f) {
  return Object.assign({}, f, { righe: serializeRighe(f.righe) });
}

// ---------------------------------------------------------------------------
// piccoli componenti di form
// ---------------------------------------------------------------------------
function Campo({ label, hint, error, children, wide }) {
  return (
    <label className={'f1-campo' + (wide ? ' wide' : '')}>
      <span className="f1-lbl">{label}</span>
      {children}
      {hint && <span className="f1-hint">{hint}</span>}
      {error && <span className="f1-fielderr">{error}</span>}
    </label>
  );
}

// Sezione avanzata collassabile (accordion custom: apertura controllata per
// il focus dei problemi di validazione) con badge 'compilato'.
function Sezione({ id, titolo, compilato, open, onToggle, children }) {
  return (
    <div className="f1-sez" id={'f1-sez-' + id}>
      <button type="button" className="f1-sez-h" aria-expanded={!!open} onClick={() => onToggle(id)}>
        <span className="f1-sez-arrow">{open ? '▾' : '▸'}</span>
        <span className="f1-sez-t">{titolo}</span>
        {compilato && <span className="f1-badge-comp">compilato</span>}
      </button>
      {open && <div className="f1-sez-body">{children}</div>}
    </div>
  );
}

// Lista generica di documenti correlati (ordine/contratto/convenzione/
// ricezione/fatture collegate) — stessa shape DatiDocumentiCorrelati.
function DocRifList({ titolo, list, onChange, pathBase }) {
  const rows = list || [];
  function patch(i, campo, val) {
    const a = rows.slice();
    a[i] = Object.assign({}, a[i], { [campo]: val });
    onChange(a);
  }
  return (
    <div className="f1-sublist">
      <div className="f1-sublist-t">{titolo}</div>
      {rows.length === 0 && <div className="f1-muted">Nessun riferimento.</div>}
      {rows.map((d, i) => (
        <div className="f1-subrow" key={i}>
          <input className="f1-in" style={{ flex: 2 }} placeholder="Numero documento" value={d.idDocumento || ''}
            data-campo={pathBase + '[' + i + '].idDocumento'}
            onChange={(e) => patch(i, 'idDocumento', e.target.value)} />
          <input className="f1-in" type="date" style={{ flex: 1 }} value={d.data || ''}
            onChange={(e) => patch(i, 'data', e.target.value || null)} />
          <input className="f1-in" style={{ flex: 1 }} placeholder="CIG" value={d.cig || ''}
            data-campo={pathBase + '[' + i + '].cig'}
            onChange={(e) => patch(i, 'cig', e.target.value)} />
          <input className="f1-in" style={{ flex: 1 }} placeholder="CUP" value={d.cup || ''}
            data-campo={pathBase + '[' + i + '].cup'}
            onChange={(e) => patch(i, 'cup', e.target.value)} />
          <input className="f1-in" style={{ flex: 1 }} placeholder="Commessa" value={d.commessa || ''}
            onChange={(e) => patch(i, 'commessa', e.target.value)} />
          <input className="f1-in" style={{ flex: 1 }} placeholder="Righe (es. 1,2)" value={joinIntList(d.riferimentoLinee)}
            onChange={(e) => patch(i, 'riferimentoLinee', parseIntList(e.target.value))} />
          <button type="button" className="f1-del" title="Rimuovi" onClick={() => onChange(rows.filter((x, j) => j !== i))}>✕</button>
        </div>
      ))}
      <button type="button" className="f1-btn gray sm" onClick={() => onChange(rows.concat([buildDocRifVuoto()]))}>+ Aggiungi</button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ClienteForm — modale form completo del cliente (DTO del contratto),
// validazioni SOLO FORMATO (editor-core.validaClienteFormato), POST/PATCH
// awaited con guardia sincrona. Usato dalla vista Clienti (FattApp) e
// inline dall'editor ('Nuovo cliente'). Si chiude in 2+ modi (X, Annulla,
// ESC, click sul velo) e MAI con perdita dati silenziosa (conferma se sporco).
// ---------------------------------------------------------------------------
export function ClienteForm({ win, cliente, onSaved, onClose, toast }) {
  const nuovo = !cliente || !cliente.id;
  const [c, setC] = React.useState(() => Object.assign({
    tipoSoggetto: 'azienda', denominazione: '', nome: '', cognome: '', titolo: '',
    codEori: '', idPaese: 'IT', partitaIva: '', codiceFiscale: '',
    codiceDestinatario: '', pec: '', indirizzo: '', numeroCivico: '', cap: '',
    comune: '', provincia: '', nazione: 'IT', isPa: false, codiceUfficioPa: '',
    splitPayment: false, note: '',
  }, cliente || {}));
  const [errors, setErrors] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  const savingRef = React.useRef(false); // guardia SINCRONA anti doppio-submit
  const dirtyRef = React.useRef(false);
  const alive = React.useRef(true);
  React.useEffect(() => () => { alive.current = false; }, []);

  // ---- autofill (VIES / geo suggest / IndicePA) ----
  const [viesBusy, setViesBusy] = React.useState(false);
  const viesBusyRef = React.useRef(false);       // guardia SINCRONA
  const [hl, setHl] = React.useState({});        // campi appena compilati da un lookup (evidenziati)
  const [sug, setSug] = React.useState(null);    // suggerimenti indirizzo: null = tendina chiusa
  const [sugIdx, setSugIdx] = React.useState(-1); // voce evidenziata con le frecce (-1 = nessuna)
  const geoQueryRef = React.useRef('');           // ultimo testo digitato (per il civico di riserva)
  const sugRef = React.useRef(null); sugRef.current = sug;
  const geoSeqRef = React.useRef(0);             // scarta le risposte fuori ordine
  const [paRes, setPaRes] = React.useState(null); // risultati IndicePA: null = pannello chiuso
  const paResRef = React.useRef(null); paResRef.current = paRes;
  const [paBusy, setPaBusy] = React.useState(false);
  const paBusyRef = React.useRef(false);
  // OpenAPI Company (SDI+PEC): servizio A PAGAMENTO. null = stato non ancora noto
  const [sdiCfg, setSdiCfg] = React.useState(null); // {configurato, ambiente}
  const [sdiBusy, setSdiBusy] = React.useState(false);
  const sdiBusyRef = React.useRef(false);          // guardia SINCRONA

  function set(campo, val) {
    dirtyRef.current = true;
    setC((p) => Object.assign({}, p, { [campo]: val }));
    // il campo torna "suo": via l'evidenza del lookup appena l'utente lo tocca
    setHl((h) => (h[campo] ? Object.assign({}, h, { [campo]: false }) : h));
  }

  // classe input con evidenza 'compilato da lookup'
  function inCls(campo) { return 'f1-in' + (hl[campo] ? ' f1-in-hl' : ''); }

  // applica un blocco di campi (da lookup) in un colpo solo, con evidenza
  function applicaCampi(fill) {
    const campi = Object.keys(fill);
    if (!campi.length) return 0;
    dirtyRef.current = true;
    setC((p) => Object.assign({}, p, fill));
    setHl((h) => { const o = Object.assign({}, h); campi.forEach((k) => { o[k] = true; }); return o; });
    return campi.length;
  }

  const tryClose = React.useCallback(() => {
    if (dirtyRef.current && !win.confirm('Chiudere senza salvare? Le modifiche al cliente andranno perse.')) return;
    onClose();
  }, [win, onClose]);

  React.useEffect(() => {
    function onKey(e) {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      // ESC chiude PRIMA gli strati interni (tendina indirizzi, pannello IndicePA)
      if (sugRef.current != null) { setSug(null); return; }
      if (paResRef.current != null) { setPaRes(null); return; }
      tryClose();
    }
    win.document.addEventListener('keydown', onKey, true);
    return () => win.document.removeEventListener('keydown', onKey, true);
  }, [win, tryClose]);

  // ---- (a) Compila da P.IVA (VIES): riempie SOLO i campi vuoti ----
  const pivaOk = /^\d{11}$/.test((c.partitaIva || '').trim());
  async function compilaDaPiva() {
    if (viesBusyRef.current) return;
    const piva = (c.partitaIva || '').trim();
    if (!/^\d{11}$/.test(piva)) return;
    viesBusyRef.current = true; setViesBusy(true);
    try {
      const r = await api(win, '/lookup-piva/' + encodeURIComponent(piva));
      if (!alive.current) return;
      if (!r || r.trovato === false) { toast('P.IVA non trovata su VIES', 'err'); return; }
      if (r.valida === false) toast('Attenzione: la P.IVA non risulta attiva', 'err');
      const fill = {};
      // denominazione solo per i soggetti azienda (persona fisica: nome+cognome, mai entrambi)
      if (c.tipoSoggetto !== 'persona' && r.denominazione && !(c.denominazione || '').trim()) fill.denominazione = r.denominazione;
      if (r.indirizzo && !(c.indirizzo || '').trim()) fill.indirizzo = r.indirizzo;
      if (r.cap && !(c.cap || '').trim()) fill.cap = r.cap;
      if (r.comune && !(c.comune || '').trim()) fill.comune = r.comune;
      if (r.provincia && !(c.provincia || '').trim()) fill.provincia = r.provincia;
      if (!(c.codiceFiscale || '').trim()) fill.codiceFiscale = piva;
      const n = applicaCampi(fill);
      if (r.valida !== false) {
        toast(n ? ('Compilati ' + n + ' campi da VIES') : 'Nessun campo da compilare: i dati sono già presenti');
      }
    } catch (e) {
      if (e.status === 502) toast('VIES non raggiungibile, riprova', 'err');
      else toast('Ricerca VIES fallita: ' + e.message, 'err');
    } finally {
      viesBusyRef.current = false;
      if (alive.current) setViesBusy(false);
    }
  }

  // ---- (a-bis) Cerca SDI e PEC (OpenAPI Company) — A PAGAMENTO ----
  // Il servizio si paga a chiamata: il bottone è ESPLICITO e non parte mai da
  // solo. Prima si chiede al server se è configurato (il token sta nell'ENV del
  // server, mai nel browser): se non lo è, il bottone resta spento con la
  // spiegazione nel title invece di far fallire l'utente contro un 501.
  React.useEffect(() => {
    let vivo = true;
    api(win, '/lookup-azienda-stato')
      .then((r) => { if (vivo && alive.current) setSdiCfg(r || { configurato: false }); })
      .catch(() => { if (vivo && alive.current) setSdiCfg({ configurato: false }); });
    return () => { vivo = false; };
  }, [win]);

  const sdiPronto = !!(sdiCfg && sdiCfg.configurato);
  const eur = (n) => Number(n || 0).toFixed(2).replace('.', ',');

  async function cercaSdiPec() {
    if (sdiBusyRef.current) return;
    const piva = (c.partitaIva || '').trim();
    if (!/^\d{11}$/.test(piva) || !sdiPronto) return;
    sdiBusyRef.current = true; setSdiBusy(true);
    try {
      const r = await api(win, '/lookup-azienda/' + encodeURIComponent(piva));
      if (!alive.current) return;
      if (!r || r.trovato === false) { toast('Nessun dato trovato per questa P.IVA', 'err'); return; }
      // SOLO i campi vuoti: quello che l'utente ha scritto non si tocca mai
      const fill = {};
      if (r.codiceDestinatario && !(c.codiceDestinatario || '').trim()) fill.codiceDestinatario = r.codiceDestinatario;
      if (r.pec && !(c.pec || '').trim()) fill.pec = r.pec;
      if (c.tipoSoggetto !== 'persona' && r.denominazione && !(c.denominazione || '').trim()) fill.denominazione = r.denominazione;
      if (r.codiceFiscale && !(c.codiceFiscale || '').trim()) fill.codiceFiscale = r.codiceFiscale;
      if (r.indirizzo && !(c.indirizzo || '').trim()) fill.indirizzo = r.indirizzo;
      if (r.cap && !(c.cap || '').trim()) fill.cap = r.cap;
      if (r.comune && !(c.comune || '').trim()) fill.comune = r.comune;
      if (r.provincia && !(c.provincia || '').trim()) fill.provincia = r.provincia;
      const n = applicaCampi(fill);
      // il costo si DICHIARA sempre: è denaro dell'utente (0 = risposta dalla cache del server)
      const costo = r.costoStimato > 0 ? ' (costo ~' + eur(r.costoStimato) + ' EUR)' : ' (nessun costo: dato già in cache)';
      if (n) toast('Compilati ' + n + ' campi' + costo);
      else toast('Nessun campo da compilare: i dati sono già presenti' + costo);
    } catch (e) {
      if (e.status === 501) toast('Servizio non configurato: attiva OpenAPI Company e imposta il token sul server', 'err');
      else if (e.status === 402) toast('Credito OpenAPI esaurito: ricarica il wallet su console.openapi.com', 'err');
      else if (e.status === 401) toast('Token OpenAPI non valido o senza permessi Company', 'err');
      else if (e.status === 503) toast('Lookup esterni temporaneamente disabilitati (quarantena)', 'err');
      else if (e.status === 502) toast('OpenAPI Company non raggiungibile, riprova', 'err');
      else toast('Ricerca SDI/PEC fallita: ' + e.message, 'err');
    } finally {
      sdiBusyRef.current = false;
      if (alive.current) setSdiBusy(false);
    }
  }

  // ---- (b) autocomplete indirizzo (debounce 400ms, min 4 caratteri) ----
  const debGeo = React.useMemo(() => debounce((q, comune) => {
    const seq = ++geoSeqRef.current;
    const query = comune ? (q + ' ' + comune) : q;
    api(win, '/geo/suggest?q=' + encodeURIComponent(query))
      .then((r) => { if (alive.current && seq === geoSeqRef.current) setSug((r && r.risultati) || []); })
      .catch(() => { if (alive.current && seq === geoSeqRef.current) setSug(null); }); // errore = niente tendina (non bloccante)
  }, 400), [win]); // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => () => debGeo.cancel(), [debGeo]);

  function onIndirizzoChange(v) {
    set('indirizzo', v);
    geoQueryRef.current = v;
    setSugIdx(-1); // testo cambiato ⇒ nessuna voce preselezionata
    if (v.trim().length >= 4) debGeo(v.trim(), (c.comune || '').trim());
    else { debGeo.cancel(); geoSeqRef.current++; setSug(null); }
  }

  // tastiera sulla tendina: ↓/↑ scorrono, Invio sceglie, Esc chiude (mai
  // navigazione a sorpresa: senza voce evidenziata Invio non fa nulla)
  function onIndirizzoKeyDown(e) {
    if (!sug || !sug.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSugIdx((n) => (n + 1) % sug.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSugIdx((n) => (n <= 0 ? sug.length - 1 : n - 1));
    } else if (e.key === 'Enter') {
      if (sugIdx >= 0 && sugIdx < sug.length) { e.preventDefault(); scegliIndirizzo(sug[sugIdx]); }
    } else if (e.key === 'Escape') {
      e.stopPropagation(); // non chiudere l'intera modale: prima si chiude la tendina
      setSug(null);
      setSugIdx(-1);
    }
  }

  // scelta esplicita dell'utente ⇒ SOVRASCRIVE (solo i campi valorizzati nel suggerimento)
  // civico digitato dall'utente nella ricerca ("via appia 120 caserta" → "120"):
  // molti risultati OSM sono a livello VIA e non portano il numero — meglio
  // riusare quello scritto che lasciare il campo vuoto o, peggio, quello vecchio.
  function civicoDaTesto(q) {
    const m = String(q || '').match(/\b(\d{1,4}(?:\s*[\/-]?\s*[a-zA-Z])?)\b/);
    return m ? m[1].replace(/\s+/g, '') : '';
  }

  function scegliIndirizzo(s) {
    // scelta ESPLICITA ⇒ i campi dell'indirizzo si allineano tutti al suggerimento:
    // quelli non forniti si AZZERANO invece di restare col valore vecchio
    // (collaudo utente 2026-07-20: il civico sembrava "non funzionale").
    const fill = {
      indirizzo: s.via || s.label || '',
      numeroCivico: s.civico || civicoDaTesto(geoQueryRef.current),
      cap: s.cap || '',
      comune: s.comune || '',
      provincia: s.provincia || '',
    };
    applicaCampi(fill);
    geoSeqRef.current++;
    setSug(null);
    setSugIdx(-1);
  }

  // ---- (c) IndicePA (solo clienti PA) ----
  async function cercaIndicePa() {
    if (paBusyRef.current) return;
    const cf = (c.codiceFiscale || c.partitaIva || '').trim();
    if (!cf) { toast('Inserisci prima il codice fiscale (o la P.IVA) della PA', 'err'); return; }
    paBusyRef.current = true; setPaBusy(true);
    try {
      const r = await api(win, '/lookup-pa?cf=' + encodeURIComponent(cf));
      if (alive.current) setPaRes((r && r.risultati) || []);
    } catch (e) {
      toast('Ricerca IndicePA fallita: ' + e.message, 'err');
    } finally {
      paBusyRef.current = false;
      if (alive.current) setPaBusy(false);
    }
  }

  function scegliUfficioPa(u) {
    const fill = {};
    if (u.codiceUfficio) fill.codiceUfficioPa = u.codiceUfficio;
    if (u.pec) fill.pec = u.pec;
    if (u.indirizzo) fill.indirizzo = u.indirizzo;
    if (u.cap) fill.cap = u.cap;
    if (u.comune) fill.comune = u.comune;
    // la denominazione NON si sovrascrive: solo se vuota (l'ufficio può avere un nome diverso dall'ente)
    if (u.denominazione && c.tipoSoggetto !== 'persona' && !(c.denominazione || '').trim()) fill.denominazione = u.denominazione;
    const n = applicaCampi(fill);
    setPaRes(null);
    toast(n ? ('Compilati ' + n + ' campi da IndicePA') : 'Nessun campo compilato');
  }

  async function salva() {
    if (savingRef.current) return;
    const err = validaClienteFormato(c);
    setErrors(err);
    if (Object.keys(err).length) { toast('Correggi i campi evidenziati', 'err'); return; }
    savingRef.current = true; setSaving(true);
    try {
      let dto;
      if (nuovo) dto = await api(win, '/clienti', { method: 'POST', body: c });
      else dto = await api(win, '/clienti/' + encodeURIComponent(cliente.id), {
        method: 'PATCH', body: Object.assign({}, c, { _expectedUpdatedAt: cliente.updatedAt }),
      });
      toast(nuovo ? 'Cliente creato' : 'Cliente aggiornato');
      dirtyRef.current = false;
      onSaved(dto);
    } catch (e) {
      if (e.status === 409) toast('NON salvato: il cliente è stato modificato da un\'altra postazione. Riapri la scheda.', 'err');
      else toast('NON salvato: ' + e.message, 'err'); // i dati RESTANO nel form
    } finally {
      savingRef.current = false;
      if (alive.current) setSaving(false);
    }
  }

  const azienda = c.tipoSoggetto !== 'persona';
  return (
    <div className="f1-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) tryClose(); }}>
      <div className="f1-modal" role="dialog" aria-label={nuovo ? 'Nuovo cliente' : 'Modifica cliente'}>
        <div className="f1-modal-h">
          <span>{nuovo ? 'Nuovo cliente' : 'Modifica cliente'}</span>
          <button type="button" className="f1-x" title="Chiudi" onClick={tryClose}>✕</button>
        </div>
        <div className="f1-modal-b">
          <div className="f1-grid">
            <Campo label="Tipo soggetto">
              <select className="f1-in" value={c.tipoSoggetto || 'azienda'} onChange={(e) => set('tipoSoggetto', e.target.value)}>
                <option value="azienda">Azienda / ente</option>
                <option value="persona">Persona fisica</option>
              </select>
            </Campo>
            {azienda ? (
              <Campo label="Denominazione *" error={errors.denominazione} wide>
                <input className={inCls('denominazione')} value={c.denominazione || ''} onChange={(e) => set('denominazione', e.target.value)} placeholder="Es. Rossi Costruzioni S.r.l." />
              </Campo>
            ) : (
              <React.Fragment>
                <Campo label="Nome *" error={errors.denominazione}>
                  <input className="f1-in" value={c.nome || ''} onChange={(e) => set('nome', e.target.value)} />
                </Campo>
                <Campo label="Cognome *">
                  <input className="f1-in" value={c.cognome || ''} onChange={(e) => set('cognome', e.target.value)} />
                </Campo>
              </React.Fragment>
            )}
            <Campo label="Paese (ID fiscale)" error={errors.idPaese}>
              <input className="f1-in" maxLength={2} value={c.idPaese || ''} onChange={(e) => set('idPaese', e.target.value.toUpperCase())} />
            </Campo>
            <Campo label="Partita IVA" error={errors.partitaIva} hint={pivaOk ? null : 'con 11 cifre si attiva «Compila da P.IVA» (VIES)'}>
              <div className="f1-in-row">
                <input className="f1-in" style={{ flex: 1 }} value={c.partitaIva || ''} onChange={(e) => set('partitaIva', e.target.value.trim())} />
                <button type="button" className="f1-btn ghost sm" disabled={viesBusy || !pivaOk}
                  title="Cerca la P.IVA su VIES e compila i campi ancora vuoti"
                  onClick={compilaDaPiva}>{viesBusy ? 'Cerco…' : 'Compila da P.IVA'}</button>
              </div>
            </Campo>
            <Campo label="Codice fiscale" error={errors.codiceFiscale}>
              <input className={inCls('codiceFiscale')} value={c.codiceFiscale || ''} onChange={(e) => set('codiceFiscale', e.target.value.trim().toUpperCase())} />
            </Campo>
            <Campo label="Codice destinatario (SDI)" error={errors.codiceDestinatario}
              hint={sdiPronto
                ? '7 caratteri; vuoto = 0000000 (PEC o area riservata) — «Cerca SDI e PEC» è a pagamento (~0,05 EUR)'
                : '7 caratteri; vuoto = 0000000 (PEC o area riservata)'}>
              <div className="f1-in-row">
                <input className={inCls('codiceDestinatario')} style={{ flex: 1 }} value={c.codiceDestinatario || ''} onChange={(e) => set('codiceDestinatario', e.target.value.trim().toUpperCase())} />
                <button type="button" className="f1-btn ghost sm" disabled={sdiBusy || !pivaOk || !sdiPronto}
                  title={!sdiPronto
                    ? 'Richiede il servizio OpenAPI Company: vedi Impostazioni'
                    : (pivaOk
                      ? 'Cerca su OpenAPI Company il codice destinatario SDI e la PEC di questa P.IVA (servizio a pagamento, ~0,05 EUR a ricerca)'
                      : 'Inserisci prima la Partita IVA (11 cifre)')}
                  onClick={cercaSdiPec}>{sdiBusy ? 'Cerco…' : 'Cerca SDI e PEC'}</button>
              </div>
            </Campo>
            <Campo label="PEC" error={errors.pec}>
              <input className={inCls('pec')} value={c.pec || ''} onChange={(e) => set('pec', e.target.value.trim())} />
            </Campo>
            <Campo label="Indirizzo" hint="digita almeno 4 caratteri; ↓ ↑ per scorrere, Invio per scegliere" wide>
              <div className="f1-combo">
                <input className={inCls('indirizzo')} value={c.indirizzo || ''}
                  onChange={(e) => onIndirizzoChange(e.target.value)}
                  onKeyDown={onIndirizzoKeyDown}
                  onBlur={() => win.setTimeout(() => { if (alive.current) { setSug(null); setSugIdx(-1); } }, 180)}
                  placeholder="Es. Via Roma 12, Caserta" autoComplete="off"
                  role="combobox" aria-expanded={sug != null && sug.length > 0} aria-autocomplete="list" />
                {sug != null && (
                  <div className="f1-combo-list" role="listbox">
                    {sug.length === 0 && <div className="f1-muted" style={{ padding: 8 }}>Nessun indirizzo trovato.</div>}
                    {sug.map((s, i) => (
                      <button type="button" key={i} className={'f1-combo-item' + (i === sugIdx ? ' sel' : '')}
                        role="option" aria-selected={i === sugIdx}
                        onMouseEnter={() => setSugIdx(i)}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => scegliIndirizzo(s)}>
                        <span>{[s.via, s.civico].filter(Boolean).join(' ') || s.label}</span>
                        <span className="f1-muted">{[s.cap, s.comune, s.provincia ? '(' + s.provincia + ')' : ''].filter(Boolean).join(' ')}</span>
                      </button>
                    ))}
                    {/* attribuzione ODbL della fonte dati (geocoding OpenStreetMap/Nominatim) */}
                    <div className="f1-muted" style={{ padding: '5px 10px', borderTop: '1px solid #f1f5f9', fontSize: 10, textAlign: 'right' }}>© OpenStreetMap</div>
                  </div>
                )}
              </div>
            </Campo>
            <Campo label="N. civico">
              <input className={inCls('numeroCivico')} value={c.numeroCivico || ''} onChange={(e) => set('numeroCivico', e.target.value)} />
            </Campo>
            <Campo label="CAP" error={errors.cap}>
              <input className={inCls('cap')} value={c.cap || ''} onChange={(e) => set('cap', e.target.value.trim())} />
            </Campo>
            <Campo label="Comune">
              <input className={inCls('comune')} value={c.comune || ''} onChange={(e) => set('comune', e.target.value)} />
            </Campo>
            <Campo label="Provincia" error={errors.provincia}>
              <input className={inCls('provincia')} maxLength={2} value={c.provincia || ''} onChange={(e) => set('provincia', e.target.value.toUpperCase())} />
            </Campo>
            <Campo label="Nazione" error={errors.nazione}>
              <input className="f1-in" maxLength={2} value={c.nazione || ''} onChange={(e) => set('nazione', e.target.value.toUpperCase())} />
            </Campo>
            <Campo label="Titolo">
              <input className="f1-in" value={c.titolo || ''} onChange={(e) => set('titolo', e.target.value)} />
            </Campo>
            <Campo label="Codice EORI">
              <input className="f1-in" value={c.codEori || ''} onChange={(e) => set('codEori', e.target.value)} />
            </Campo>
            <Campo label="Pubblica Amministrazione">
              <label className="f1-check">
                <input type="checkbox" checked={!!c.isPa} onChange={(e) => set('isPa', e.target.checked)} />
                <span>è una PA (formato FPA12)</span>
              </label>
            </Campo>
            {!!c.isPa && (
              <Campo label="Codice ufficio PA (iPA)" error={errors.codiceUfficioPa}>
                <div className="f1-in-row">
                  <input className={inCls('codiceUfficioPa')} style={{ flex: 1 }} maxLength={6} value={c.codiceUfficioPa || ''} onChange={(e) => set('codiceUfficioPa', e.target.value.trim().toUpperCase())} />
                  <button type="button" className="f1-btn ghost sm" disabled={paBusy}
                    title="Cerca gli uffici di fatturazione su IndicePA (per codice fiscale o P.IVA)"
                    onClick={cercaIndicePa}>{paBusy ? 'Cerco…' : 'Cerca su IndicePA'}</button>
                </div>
              </Campo>
            )}
            {!!c.isPa && paRes != null && (
              <div className="f1-pa-res">
                <div className="f1-pa-res-h">
                  <span>{paRes.length === 0 ? 'IndicePA: nessun ufficio trovato per questo CF/P.IVA' : 'IndicePA — scegli l’ufficio di fatturazione:'}</span>
                  <button type="button" className="f1-x" title="Chiudi" onClick={() => setPaRes(null)}>✕</button>
                </div>
                {paRes.map((u, i) => (
                  <button type="button" key={i} className="f1-combo-item"
                    onClick={() => scegliUfficioPa(u)}>
                    <span>{u.denominazione || '—'}<span className="f1-muted">{' · ufficio ' + (u.codiceUfficio || '—')}</span></span>
                    <span className="f1-muted">{u.pec || 'senza PEC'}</span>
                  </button>
                ))}
              </div>
            )}
            <Campo label="Split payment">
              <label className="f1-check">
                <input type="checkbox" checked={!!c.splitPayment} onChange={(e) => set('splitPayment', e.target.checked)} />
                <span>scissione dei pagamenti (art.17-ter)</span>
              </label>
            </Campo>
            <Campo label="Note" wide>
              <textarea className="f1-in" rows={2} value={c.note || ''} onChange={(e) => set('note', e.target.value)} />
            </Campo>
          </div>
        </div>
        <div className="f1-modal-f">
          <button type="button" className="f1-btn" disabled={saving} onClick={salva}>{saving ? 'Salvo…' : 'Salva cliente'}</button>
          <button type="button" className="f1-btn gray" onClick={tryClose}>Annulla</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PianoRateForm — mini-form 'Genera piano rate…' (modale). Tutto locale: alla
// conferma chiama generaRate (rate-core, puro) e passa il risultato al
// chiamante (onGenera, che conferma la sostituzione e chiude). Errori del
// generatore mostrati INLINE senza perdere i dati inseriti; si chiude in 2+
// modi (X, Annulla, ESC, click sul velo) con conferma se sporco.
// ---------------------------------------------------------------------------
function PianoRateForm({ win, nettoDefault, primaScadenzaDefault, onGenera, onClose }) {
  const [p, setP] = React.useState(() => ({
    importoTotale: (nettoDefault != null && nettoDefault > 0) ? String(nettoDefault) : '',
    modo: 'numero', numeroRate: '2', importoRata: '',
    dataPrima: primaScadenzaDefault || '', cadenza: 'mensile', giornoDelMese: '',
  }));
  const [err, setErr] = React.useState(null);
  const dirtyRef = React.useRef(false);

  function set(k, v) { dirtyRef.current = true; setErr(null); setP((x) => Object.assign({}, x, { [k]: v })); }

  const tryClose = React.useCallback(() => {
    if (dirtyRef.current && !win.confirm('Chiudere il generatore senza applicare il piano rate?')) return;
    onClose();
  }, [win, onClose]);

  React.useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') { e.stopPropagation(); tryClose(); } }
    win.document.addEventListener('keydown', onKey, true);
    return () => win.document.removeEventListener('keydown', onKey, true);
  }, [win, tryClose]);

  function genera() {
    const params = {
      importoTotale: numOrNull(p.importoTotale),
      modo: p.modo,
      dataPrima: p.dataPrima,
      cadenza: p.cadenza,
      giornoDelMese: p.giornoDelMese === '' ? null : Number(p.giornoDelMese),
    };
    if (p.modo === 'numero') params.numeroRate = numOrNull(p.numeroRate);
    else params.importoRata = numOrNull(p.importoRata);
    const res = generaRate(params);
    if (res.errore) { setErr(res.errore); return; } // i dati restano nel form
    onGenera(res.rate); // il chiamante conferma la sostituzione e, se ok, chiude
  }

  return (
    <div className="f1-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) tryClose(); }}>
      <div className="f1-modal" role="dialog" aria-label="Genera piano rate" style={{ maxWidth: 460 }}>
        <div className="f1-modal-h">
          <span>Genera piano rate</span>
          <button type="button" className="f1-x" title="Chiudi" onClick={tryClose}>✕</button>
        </div>
        <div className="f1-modal-b">
          <div className="f1-grid">
            <Campo label="Importo totale" hint="precompilato col netto a pagare" wide>
              <input className="f1-in num" type="number" step="any" value={p.importoTotale}
                onChange={(e) => set('importoTotale', e.target.value)} />
            </Campo>
            <div className="f1-campo wide">
              <span className="f1-lbl">Come dividere</span>
              <label className="f1-check">
                <input type="radio" name="f1-piano-modo" checked={p.modo === 'numero'} onChange={() => set('modo', 'numero')} />
                <span>Numero di rate uguali</span>
              </label>
              <label className="f1-check">
                <input type="radio" name="f1-piano-modo" checked={p.modo === 'importo'} onChange={() => set('modo', 'importo')} />
                <span>Importo fisso per rata (l&rsquo;ultima è il resto)</span>
              </label>
            </div>
            {p.modo === 'numero' ? (
              <Campo label="Numero rate">
                <input className="f1-in num" type="number" min="1" max="60" step="1" value={p.numeroRate}
                  onChange={(e) => set('numeroRate', e.target.value)} />
              </Campo>
            ) : (
              <Campo label="Importo per rata">
                <input className="f1-in num" type="number" step="any" value={p.importoRata}
                  onChange={(e) => set('importoRata', e.target.value)} />
              </Campo>
            )}
            <Campo label="Prima scadenza">
              <input className="f1-in" type="date" value={p.dataPrima}
                onChange={(e) => set('dataPrima', e.target.value)} />
            </Campo>
            <Campo label="Cadenza">
              <select className="f1-in" value={p.cadenza} onChange={(e) => set('cadenza', e.target.value)}>
                <option value="mensile">Mensile</option>
                <option value="bimestrale">Bimestrale</option>
                <option value="trimestrale">Trimestrale</option>
              </select>
            </Campo>
            <Campo label="Giorno del mese" hint="1-28, facoltativo: vuoto = stesso giorno della prima scadenza">
              <input className="f1-in num" type="number" min="1" max="28" step="1" value={p.giornoDelMese}
                onChange={(e) => set('giornoDelMese', e.target.value)} />
            </Campo>
          </div>
          {err && <div className="f1-banner err" role="alert" style={{ marginTop: 10, marginBottom: 0 }}>{err}</div>}
        </div>
        <div className="f1-modal-f">
          <button type="button" className="f1-btn" onClick={genera}>Genera e sostituisci le rate</button>
          <button type="button" className="f1-btn gray" onClick={tryClose}>Annulla</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Editor
// ---------------------------------------------------------------------------
export function Editor({ win, fatturaId, onClose, toast, registerGuard }) {
  const doc = win.document;
  const [fatt, setFatt] = React.useState(null);
  const [loadState, setLoadState] = React.useState('loading'); // loading|error|ok
  const [loadErr, setLoadErr] = React.useState(null);
  const [clienti, setClienti] = React.useState([]);
  const [voci, setVoci] = React.useState([]);
  const [numeroPrev, setNumeroPrev] = React.useState(null);
  const [save, setSave] = React.useState({ kind: 'idle' }); // idle|saving|saved|error|conflict
  const [conflict, setConflict] = React.useState(null);
  const [problemi, setProblemi] = React.useState(null);
  const [xml, setXml] = React.useState(null);
  const [open, setOpen] = React.useState({});
  const [drawer, setDrawer] = React.useState(null); // indice riga del drawer avanzato
  const [cliFormOpen, setCliFormOpen] = React.useState(false);
  const [cliQuery, setCliQuery] = React.useState('');
  const [cliOpen, setCliOpen] = React.useState(false);
  const [validando, setValidando] = React.useState(false);
  const [pianoOpen, setPianoOpen] = React.useState(false); // mini-form 'Genera piano rate…'
  const [pdfBusy, setPdfBusy] = React.useState(false);
  const [contBusy, setContBusy] = React.useState(false);
  const [inviando, setInviando] = React.useState(false);

  // ref SINCRONI (setState è async): guardie e specchi per autosave/handler
  const fattRef = React.useRef(null); fattRef.current = fatt;
  const saveRef = React.useRef(save); saveRef.current = save;
  const dirtyRef = React.useRef({ testata: false, righe: false });
  const savingRef = React.useRef(false);
  const pendingRef = React.useRef(false);
  const suspendRef = React.useRef(false);   // 409: autosave SOSPESO
  const validandoRef = React.useRef(false);
  const xmlBusyRef = React.useRef(false);
  const pdfBusyRef = React.useRef(false);
  const contBusyRef = React.useRef(false); // ponte verso Contabilità
  const inviandoRef = React.useRef(false); // invio allo SDI: guardia anti doppio-click
  const importoToccatoRef = React.useRef(false); // rata: importo modificato a mano
  const keySeqRef = React.useRef(0);
  const alive = React.useRef(true);
  React.useEffect(() => () => { alive.current = false; }, []);

  const nextKey = React.useCallback(() => 'k' + (++keySeqRef.current), []);

  const withRowKeys = React.useCallback((righe, prev) => {
    return (righe || []).map((r, i) => {
      const p = prev && prev[i];
      return Object.assign({}, r, { _key: (p && p._key) || nextKey() });
    });
  }, [nextKey]);

  // ---- caricamento iniziale ----
  const load = React.useCallback(async () => {
    setLoadState('loading'); setLoadErr(null);
    try {
      let rec;
      if (fatturaId) {
        rec = await api(win, '/fatture/' + encodeURIComponent(fatturaId));
        importoToccatoRef.current = !!(rec.pagamento && rec.pagamento.rate
          && (rec.pagamento.rate.length > 1 // piano a tranche: MAI automatismi sull'importo
            || (rec.pagamento.rate[0] && rec.pagamento.rate[0].importo != null
              && rec.pagamento.rate[0].importo !== rec.nettoAPagare)));
      } else {
        rec = buildFatturaVuota(oggiISO());
        importoToccatoRef.current = false;
      }
      if (!alive.current) return;
      rec = Object.assign({}, rec, { righe: withRowKeys(rec.righe && rec.righe.length ? rec.righe : [buildRigaVuota()]) });
      dirtyRef.current = { testata: false, righe: false };
      suspendRef.current = false;
      setFatt(rec);
      setLoadState('ok');
    } catch (e) {
      if (!alive.current) return;
      setLoadErr(e.message);
      setLoadState('error');
    }
  }, [win, fatturaId, withRowKeys]);

  const loadClienti = React.useCallback(async () => {
    try {
      const res = await api(win, '/clienti');
      if (alive.current) setClienti((res && res.clienti) || []);
    } catch (e) { /* il combobox mostra 'lista non caricata' — errore non bloccante */ }
  }, [win]);

  const loadVoci = React.useCallback(async () => {
    try {
      const res = await api(win, '/voci');
      if (alive.current) setVoci((res && res.voci) || []);
    } catch (e) { /* datalist senza suggerimenti: non bloccante */ }
  }, [win]);

  React.useEffect(() => { load(); loadClienti(); loadVoci(); }, [load, loadClienti, loadVoci]);

  // anteprima numero (SOLO informativa, il numero si conia all'invio — F2)
  const serie = fatt ? (fatt.serie || '') : '';
  const anno = fatt ? (fatt.anno || parseInt(String(fatt.dataEmissione || '').slice(0, 4), 10) || new Date().getFullYear()) : null;
  React.useEffect(() => {
    if (!anno) return;
    let dead = false;
    api(win, '/numero-anteprima?serie=' + encodeURIComponent(serie) + '&anno=' + encodeURIComponent(anno))
      .then((r) => { if (!dead && alive.current) setNumeroPrev(r && r.prossimo); })
      .catch(() => { if (!dead && alive.current) setNumeroPrev(null); });
    return () => { dead = true; };
  }, [win, serie, anno]);

  // ---- salvataggio (POST alla prima modifica, poi PATCH testata / PUT righe) ----
  const doSave = React.useCallback(async () => {
    if (suspendRef.current) return;                       // conflitto aperto: autosave sospeso
    if (savingRef.current) { pendingRef.current = true; return; }
    const f = fattRef.current;
    if (!f) return;
    const wantTestata = dirtyRef.current.testata;
    const wantRighe = dirtyRef.current.righe;
    if (!wantTestata && !wantRighe) return;
    savingRef.current = true;
    setSave({ kind: 'saving' });
    dirtyRef.current = { testata: false, righe: false };
    try {
      let rec = null;
      if (!f.id) {
        // prima scrittura: POST della testata, poi la lista INTERA delle righe
        rec = await api(win, '/fatture', { method: 'POST', body: serializeTestata(f) });
        rec = await api(win, '/fatture/' + encodeURIComponent(rec.id) + '/righe', {
          method: 'PUT',
          body: { righe: serializeRighe(fattRef.current.righe), _expectedUpdatedAt: rec.updatedAt },
        });
      } else {
        let updatedAt = f.updatedAt;
        if (wantTestata) {
          rec = await api(win, '/fatture/' + encodeURIComponent(f.id), {
            method: 'PATCH',
            body: Object.assign(serializeTestata(f), { _expectedUpdatedAt: updatedAt }),
          });
          updatedAt = rec.updatedAt;
        }
        if (wantRighe) {
          rec = await api(win, '/fatture/' + encodeURIComponent(f.id) + '/righe', {
            method: 'PUT',
            body: { righe: serializeRighe(fattRef.current.righe), _expectedUpdatedAt: updatedAt },
          });
        }
      }
      if (!alive.current) return;
      if (rec) applyServer(rec);
      setSave({ kind: 'saved', at: new Date() });
    } catch (e) {
      // i flag dirty TORNANO su: i dati non salvati restano dichiarati tali
      dirtyRef.current.testata = dirtyRef.current.testata || wantTestata;
      dirtyRef.current.righe = dirtyRef.current.righe || wantRighe;
      if (!alive.current) return;
      if (e.status === 409 && e.data && e.data.error === 'conflict') {
        suspendRef.current = true;
        setConflict((e.data && e.data.current) || {});
        setSave({ kind: 'conflict' });
      } else {
        setSave({ kind: 'error', msg: e.message });
      }
    } finally {
      savingRef.current = false;
      if (pendingRef.current) { pendingRef.current = false; deb(); }
    }
  }, [win]); // eslint-disable-line react-hooks/exhaustive-deps

  const doSaveRef = React.useRef(doSave); doSaveRef.current = doSave;
  // debounce STABILE (autosave 2s); deb() rischedula, deb.cancel/flush dai flussi espliciti
  const deb = React.useMemo(() => debounce(() => { doSaveRef.current(); }, 2000), []);
  React.useEffect(() => () => deb.cancel(), [deb]);

  // merge della risposta server: i campi CALCOLATI vincono SEMPRE; i campi
  // editabili vincono solo se l'utente non ha ri-modificato durante il volo.
  function applyServer(rec) {
    setFatt((local) => {
      if (!local) return local;
      const d = dirtyRef.current;
      if (!d.testata && !d.righe) {
        return Object.assign({}, rec, { righe: withRowKeys(rec.righe || [], local.righe) });
      }
      const out = Object.assign({}, local, {
        id: rec.id, version: rec.version, updatedAt: rec.updatedAt, createdAt: rec.createdAt,
        stato: rec.stato, anno: rec.anno, numero: rec.numero, numeroCustom: rec.numeroCustom,
        riepiloghi: rec.riepiloghi || [],
        totaleImponibile: rec.totaleImponibile, totaleImposta: rec.totaleImposta,
        totaleDocumento: rec.totaleDocumento, totaleRitenute: rec.totaleRitenute,
        nettoAPagare: rec.nettoAPagare, bolloProposto: rec.bolloProposto,
        cedente: rec.cedente, cessionario: rec.cessionario, ruoliInvertiti: rec.ruoliInvertiti,
      });
      if (!d.righe && Array.isArray(rec.righe)) out.righe = withRowKeys(rec.righe, local.righe);
      return out;
    });
  }

  // flush: annulla il debounce e salva SUBITO fino a coda vuota (usato da
  // Salva/Valida/Anteprima; tetto di giri per non ciclare su errore persistente)
  const flushSave = React.useCallback(async () => {
    deb.cancel();
    let guard = 0;
    while (guard < 6 && (savingRef.current || dirtyRef.current.testata || dirtyRef.current.righe)) {
      if (savingRef.current) await new Promise((res) => setTimeout(res, 150));
      else {
        await doSaveRef.current();
        if (saveRef.current.kind === 'error' || saveRef.current.kind === 'conflict') break;
      }
      guard++;
    }
  }, [deb]);

  // ---- patch helpers (ogni modifica = dirty + autosave) ----
  function patchTestata(campo, val) {
    dirtyRef.current.testata = true;
    setFatt((f) => Object.assign({}, f, { [campo]: val }));
    deb();
  }
  function patchDG(campo, val) {
    dirtyRef.current.testata = true;
    setFatt((f) => Object.assign({}, f, { datiGenerali: Object.assign({}, f.datiGenerali, { [campo]: val }) }));
    deb();
  }
  function patchDGFn(campo, fn) {
    dirtyRef.current.testata = true;
    setFatt((f) => {
      const dg = f.datiGenerali || {};
      return Object.assign({}, f, { datiGenerali: Object.assign({}, dg, { [campo]: fn(dg[campo]) }) });
    });
    deb();
  }
  function patchRighe(fn) {
    dirtyRef.current.righe = true;
    setFatt((f) => Object.assign({}, f, { righe: fn(f.righe) }));
    deb();
  }
  function patchRiga(i, campo, val) {
    patchRighe((righe) => {
      const a = righe.slice();
      a[i] = Object.assign({}, a[i], { [campo]: val });
      return a;
    });
  }
  function patchPagamento(fn) {
    dirtyRef.current.testata = true;
    setFatt((f) => Object.assign({}, f, { pagamento: fn(f.pagamento || buildPagamentoVuoto()) }));
    deb();
  }
  function patchRata(i, campo, val) {
    patchPagamento((p) => {
      const rate = (p.rate || [buildRataVuota()]).slice();
      rate[i] = Object.assign({}, rate[i], { [campo]: val });
      return Object.assign({}, p, { rate });
    });
  }

  // ---- rate multiple (pagamento a tranche) ----
  function addRata() {
    const p = fattRef.current.pagamento || buildPagamentoVuoto();
    const rateCur = (p.rate && p.rate.length) ? p.rate : [buildRataVuota()];
    const prima = rateCur[0] || buildRataVuota();
    const diventaTranche = p.condizioni !== 'TP01'; // dopo l'aggiunta le rate sono ≥2
    patchPagamento((pp) => {
      const rr = ((pp.rate && pp.rate.length) ? pp.rate : [buildRataVuota()]).slice();
      // nuova rata: modalità e IBAN precompilati dalla prima
      rr.push(Object.assign({}, buildRataVuota(), { modalita: prima.modalita || 'MP05', iban: prima.iban || '' }));
      const next = Object.assign({}, pp, { rate: rr });
      if (rr.length > 1) next.condizioni = 'TP01';
      return next;
    });
    importoToccatoRef.current = true; // da qui in poi gli importi sono dell'utente
    if (diventaTranche) toast('Più rate: condizioni impostate automaticamente a TP01 (pagamento a rate)');
  }

  function delRata(i) {
    const rateCur = ((fattRef.current.pagamento || {}).rate) || [];
    if (rateCur.length <= 1) return; // almeno una rata resta sempre
    const rt = rateCur[i];
    const piena = rt && (rt.importo != null || rt.dataScadenza);
    if (piena && !win.confirm('Rimuovere la rata ' + (i + 1)
      + (rt.importo != null ? (' da ' + fmtEuro(rt.importo)) : '') + '?')) return;
    // tornando a 1 rata NON si cambia nulla in automatico (né condizioni né importi)
    patchPagamento((pp) => Object.assign({}, pp, { rate: (pp.rate || []).filter((x, j) => j !== i) }));
  }

  // piano generato dal mini-form ⇒ SOSTITUISCE le rate (conferma se già presenti).
  // Ritorna false se l'utente annulla (il mini-form resta aperto).
  function applicaPianoRate(rateGen) {
    const p = fattRef.current.pagamento || buildPagamentoVuoto();
    const rateCur = p.rate || [];
    const esistono = rateCur.length > 1
      || (rateCur[0] && (rateCur[0].dataScadenza || importoToccatoRef.current));
    if (esistono && !win.confirm('Sostituire ' + (rateCur.length === 1 ? 'la rata attuale' : ('le ' + rateCur.length + ' rate attuali'))
      + ' con il nuovo piano di ' + rateGen.length + (rateGen.length === 1 ? ' rata' : ' rate') + '?')) return false;
    const prima = rateCur[0] || buildRataVuota();
    const diventaTranche = rateGen.length > 1 && p.condizioni !== 'TP01';
    patchPagamento((pp) => {
      const nuove = rateGen.map((r) => Object.assign({}, buildRataVuota(), {
        modalita: prima.modalita || 'MP05', iban: prima.iban || '',
        dataScadenza: r.dataScadenza, importo: r.importo,
      }));
      const next = Object.assign({}, pp, { rate: nuove });
      if (nuove.length > 1) next.condizioni = 'TP01';
      return next;
    });
    importoToccatoRef.current = true; // il piano è una scelta esplicita: niente ri-sync col netto
    setPianoOpen(false);
    toast('Piano di ' + rateGen.length + (rateGen.length === 1 ? ' rata generato' : ' rate generato')
      + (diventaTranche ? ' — condizioni impostate a TP01 (pagamento a rate)' : ''));
    return true;
  }

  // ---- anteprima PDF (jsPDF self-hosted, caricato on-demand) ----
  function loadJsPdf() {
    if (win.jspdf && win.jspdf.jsPDF) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const d = win.document;
      // pattern loadDeps di contabilita-module.js: script tag same-origin + attesa;
      // se un altro modulo lo sta già caricando si aspetta senza duplicare il tag
      if (!d.querySelector('script[data-fatt-dep="jspdf"]') && !d.querySelector('script[data-cont-dep="/jspdf.umd.min.js"]')) {
        const s = d.createElement('script');
        s.src = '/jspdf.umd.min.js';
        s.dataset.fattDep = 'jspdf';
        s.onerror = () => { /* la poll sotto scade e segnala */ };
        d.head.appendChild(s);
      }
      let tries = 100; // fino a ~5s
      (function waitFor() {
        if (win.jspdf && win.jspdf.jsPDF) return resolve();
        if (--tries <= 0) return reject(new Error('libreria PDF non caricata (jspdf.umd.min.js)'));
        win.setTimeout(waitFor, 50);
      })();
    });
  }

  // INVIO allo SDI. È l'azione irreversibile del modulo: si chiede conferma
  // mostrando cliente e importo, perché dopo la trasmissione si corregge solo
  // con una nota di credito. La protezione vera contro il doppio invio sta nel
  // server (claim atomico): questa guardia serve solo a non far partire due
  // richieste dallo stesso click.
  async function invia() {
    if (inviandoRef.current) return;
    const f = fattRef.current;
    if (!f.id) { toast('Salva la bozza prima di inviare', 'err'); return; }
    const chi = clienteSel ? nomeCliente(clienteSel) : (f.cessionario ? nomeCliente(f.cessionario) : 'cliente non indicato');
    const quanto = fmtEuro(tot.totaleDocumento || 0);
    if (!win.confirm('Inviare la fattura allo SDI?\n\nCliente: ' + chi + '\nTotale: ' + quanto
      + '\n\nDopo la trasmissione la fattura non si modifica più: si corregge solo con una nota di credito.')) return;

    inviandoRef.current = true; setInviando(true);
    try {
      await flushSave();
      const r = await api(win, '/fatture/' + encodeURIComponent(f.id) + '/invia', { method: 'POST' });
      toast('Inviata allo SDI — numero ' + (r && r.numero ? r.numero : '') + (r && r.ambiente === 'test' ? ' (ambiente di prova)' : ''));
      await ricaricaVersione();
    } catch (e) {
      const d = (e && e.data) || {};
      // il numero resta riservato: dirlo evita il panico da "ho bruciato un numero"
      const coda = d.nota ? ' — ' + d.nota : '';
      toast('NON inviata: ' + (d.error || e.message) + coda, 'err');
      if (Array.isArray(d.problemi) && d.problemi.length) setProblemi(d.problemi);
      await ricaricaVersione();
    } finally {
      inviandoRef.current = false;
      if (alive.current) setInviando(false);
    }
  }

  // Porta la fattura in Contabilità → Fatture (tag 'OpenAPI'). Il server rifiuta
  // con 409 se non è ancora fiscalmente emessa, e non duplica ciò che c'è già.
  async function registraInContabilita() {
    if (contBusyRef.current) return; // guardia SINCRONA anti doppio-click
    contBusyRef.current = true; setContBusy(true);
    try {
      await flushSave();
      const f = fattRef.current;
      if (!f.id) { toast('Salva la bozza prima di registrarla', 'err'); return; }
      const r = await api(win, '/fatture/' + encodeURIComponent(f.id) + '/registra-cont', { method: 'POST' });
      if (r && r.esito === 'duplicata') toast('Già presente in Contabilità: ' + (r.motivo || ''), 'warn');
      else toast('Registrata in Contabilità (tag ' + (r && r.tag ? r.tag : 'OpenAPI') + ')' + (r && r.anagraficaCreata ? ' — creata anche l\'anagrafica' : ''));
    } catch (e) {
      if (e.status === 409) toast('NON registrata: ' + (e.data && e.data.error ? e.data.error : e.message), 'err');
      else toast('NON registrata: ' + e.message, 'err');
    } finally {
      contBusyRef.current = false;
      if (alive.current) setContBusy(false);
    }
  }

  async function anteprimaPdf() {
    if (pdfBusyRef.current) return; // guardia SINCRONA anti doppio-click
    pdfBusyRef.current = true; setPdfBusy(true);
    try {
      await flushSave();
      const f = fattRef.current;
      if (!f.id) { toast('Compila la bozza prima dell\'anteprima', 'err'); return; }
      if (saveRef.current.kind === 'error' || saveRef.current.kind === 'conflict') {
        toast('Risolvi prima il salvataggio: l\'anteprima usa i dati salvati', 'err'); return;
      }
      // validazione SOLO informativa: l'anteprima di cortesia esce comunque
      let haErrori = false;
      try {
        const res = await api(win, '/fatture/' + encodeURIComponent(f.id) + '/valida', { method: 'POST' });
        haErrori = ((res && res.problemi) || []).some((p) => p.livello === 'errore');
      } catch (e) { /* best-effort: senza validazione si stampa e basta */ }
      await loadJsPdf();
      const pdfDoc = buildFatturaPdf(win.jspdf.jsPDF, serializeForCalc(fattRef.current));
      if (haErrori) toast('Anteprima con dati incompleti: la validazione segnala errori', 'err');
      let opened = null;
      try { opened = win.open(pdfDoc.output('bloburl'), '_blank'); } catch (e) { opened = null; }
      // popup bloccato ⇒ download diretto (nome con numero, o 'bozza' se non ancora coniato)
      if (!opened) pdfDoc.save('anteprima-fattura-' + ((f.numeroCustom || f.numero) || 'bozza') + '.pdf');
    } catch (e) {
      toast('Anteprima PDF fallita: ' + e.message, 'err');
    } finally {
      pdfBusyRef.current = false;
      if (alive.current) setPdfBusy(false);
    }
  }

  // on-blur: le modifiche in coda si scrivono SUBITO (autosave on-blur del piano)
  function onFieldBlur() {
    if (dirtyRef.current.testata || dirtyRef.current.righe) { deb.cancel(); doSaveRef.current(); }
  }

  // ---- righe ----
  function addRiga() {
    patchRighe((righe) => righe.concat([Object.assign({}, buildRigaVuota(), { _key: nextKey() })]));
  }
  function delRiga(i) {
    const r = fattRef.current.righe[i];
    const piena = r && (r.descrizione || r.prezzoUnitario);
    if (piena && !win.confirm('Eliminare la riga «' + (r.descrizione || 'senza descrizione') + '»?')) return;
    patchRighe((righe) => {
      const a = righe.filter((x, j) => j !== i);
      return a.length ? a : [Object.assign({}, buildRigaVuota(), { _key: nextKey() })];
    });
  }
  function moveRiga(i, dir) {
    patchRighe((righe) => {
      const j = i + dir;
      if (j < 0 || j >= righe.length) return righe;
      const a = righe.slice();
      const t = a[i]; a[i] = a[j]; a[j] = t;
      return a;
    });
  }
  // Tab/Invio sull'ultima cella dell'ULTIMA riga ⇒ nuova riga
  function onUltimaCellaKey(e, isLastRow) {
    if (!isLastRow) return;
    if (e.key === 'Enter' || (e.key === 'Tab' && !e.shiftKey)) {
      if (e.key === 'Enter') e.preventDefault();
      addRiga();
    }
  }
  // descrizione con suggerimenti: match ESATTO su un titolo di voce ⇒ prefill
  // dei soli campi ancora al default (mai sovrascrittura silenziosa)
  function onDescrizione(i, val) {
    patchRighe((righe) => {
      const a = righe.slice();
      let r = Object.assign({}, a[i], { descrizione: val });
      const v = voci.filter((x) => x.titolo === val)[0];
      if (v) {
        if (!r.prezzoUnitario) r.prezzoUnitario = v.prezzoUnitario != null ? v.prezzoUnitario : r.prezzoUnitario;
        if (!r.unitaMisura) r.unitaMisura = v.unitaMisura || '';
        if (v.aliquotaIva != null) r.aliquotaIva = v.aliquotaIva;
        if (v.natura) r.natura = v.natura;
        if (v.descrizione) r.descrizione = v.descrizione;
      }
      a[i] = r;
      return a;
    });
  }

  // ---- ritenute: attivazione con prompt 'applica a tutte le righe?' ----
  function addRitenuta() {
    const prima = ((fattRef.current.datiGenerali || {}).ritenute || []).length === 0;
    patchDGFn('ritenute', (arr) => (arr || []).concat([{ tipo: 'RT01', importo: null, aliquota: 20, causale: 'A' }]));
    if (prima && win.confirm('Applicare la ritenuta a TUTTE le righe della fattura? (imposta il flag ritenuta su ogni riga)')) {
      patchRighe((righe) => righe.map((r) => Object.assign({}, r, { ritenuta: true })));
    }
  }

  // ---- conflitto 409 (le 2 azioni del piano) ----
  async function ricaricaVersione() {
    try {
      const rec = await api(win, '/fatture/' + encodeURIComponent(fattRef.current.id));
      if (!alive.current) return;
      dirtyRef.current = { testata: false, righe: false };
      suspendRef.current = false;
      importoToccatoRef.current = true; // dopo un conflitto niente automatismi sorprendenti
      setFatt(Object.assign({}, rec, { righe: withRowKeys(rec.righe || []) }));
      setConflict(null);
      setSave({ kind: 'idle' });
      toast('Bozza ricaricata dalla versione più recente');
    } catch (e) {
      toast('Ricarica fallita: ' + e.message, 'err');
    }
  }
  async function continuaMiaCopia() {
    let cur = conflict;
    if (!cur || !cur.updatedAt) {
      try { cur = await api(win, '/fatture/' + encodeURIComponent(fattRef.current.id)); }
      catch (e) { toast('Impossibile leggere la versione corrente: ' + e.message, 'err'); return; }
    }
    if (!alive.current) return;
    setFatt((f) => Object.assign({}, f, { updatedAt: cur.updatedAt, version: cur.version }));
    dirtyRef.current = { testata: true, righe: true };
    suspendRef.current = false;
    setConflict(null);
    setSave({ kind: 'idle' });
    win.setTimeout(() => { doSaveRef.current(); }, 0);
  }

  // ---- valida / anteprima XML (guardie sincrone anti doppio-click) ----
  async function valida() {
    if (validandoRef.current) return;
    validandoRef.current = true; setValidando(true);
    try {
      await flushSave();
      const f = fattRef.current;
      if (!f.id) { toast('Compila la bozza prima di validare (si salva da sola)', 'err'); return; }
      if (saveRef.current.kind === 'error' || saveRef.current.kind === 'conflict') {
        toast('Risolvi prima il salvataggio: la validazione gira sui dati salvati', 'err'); return;
      }
      const res = await api(win, '/fatture/' + encodeURIComponent(f.id) + '/valida', { method: 'POST' });
      if (alive.current) setProblemi((res && res.problemi) || []);
    } catch (e) {
      toast('Validazione fallita: ' + e.message, 'err');
    } finally {
      validandoRef.current = false;
      if (alive.current) setValidando(false);
    }
  }

  async function anteprimaXml() {
    if (xmlBusyRef.current) return;
    xmlBusyRef.current = true;
    try {
      await flushSave();
      const f = fattRef.current;
      if (!f.id) { toast('Compila la bozza prima dell\'anteprima', 'err'); return; }
      const text = await api(win, '/fatture/' + encodeURIComponent(f.id) + '/xml-preview', { raw: true });
      if (alive.current) setXml(text);
    } catch (e) {
      if (e.status === 422 && e.data && e.data.problemi) {
        if (alive.current) setProblemi(e.data.problemi);
        toast('XML bloccato dalla validazione: correggi gli errori segnalati', 'err');
      } else {
        toast('Anteprima fallita: ' + e.message, 'err');
      }
    } finally {
      xmlBusyRef.current = false;
    }
  }

  // problema cliccato ⇒ apri la sezione giusta e focalizza il campo
  function focusProblema(p) {
    const sez = mappaCampoSezione(p.campo);
    if (SEZIONI_AVANZATE.indexOf(sez) !== -1) setOpen((o) => Object.assign({}, o, { [sez]: true }));
    win.setTimeout(() => {
      const root = doc.getElementById('fatt-root') || doc;
      let el = null;
      // la natura di riga vive nel selettore IVA combinato (nessun campo natura dedicato)
      const campo = String(p.campo).replace(/^(righe\[\d+\])\.natura$/, '$1.aliquotaIva');
      try { el = root.querySelector('[data-campo="' + campo.replace(/"/g, '\\"') + '"]'); } catch (e) { /* selettore ostile */ }
      if (!el) el = doc.getElementById('f1-sez-' + sez);
      if (el) {
        if (el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (el.focus) try { el.focus({ preventScroll: true }); } catch (e) { /* elemento non focalizzabile */ }
      }
    }, 80);
  }

  // ---- uscita con conferma (guardia per FattApp + ESC) ----
  const unsavedNow = React.useCallback(() => {
    return !!(dirtyRef.current.testata || dirtyRef.current.righe || savingRef.current
      || saveRef.current.kind === 'error' || saveRef.current.kind === 'conflict');
  }, []);
  const tryLeave = React.useCallback(() => {
    if (!unsavedNow()) return true;
    return win.confirm('Ci sono modifiche NON ancora salvate sul server. Uscire comunque? (le modifiche non salvate andranno perse)');
  }, [win, unsavedNow]);
  React.useEffect(() => {
    if (typeof registerGuard !== 'function') return undefined;
    registerGuard(tryLeave);
    return () => registerGuard(null);
  }, [registerGuard, tryLeave]);

  function chiudi() { if (tryLeave()) onClose(); }

  React.useEffect(() => {
    function onKey(e) {
      if (e.key !== 'Escape') return;
      if (cliFormOpen) return;            // la modale cliente gestisce il suo ESC
      if (pianoOpen) return;              // il mini-form piano rate gestisce il suo ESC
      if (xml != null) { setXml(null); return; }
      if (drawer != null) { setDrawer(null); return; }
      chiudi();
    }
    doc.addEventListener('keydown', onKey);
    return () => doc.removeEventListener('keydown', onKey);
  }, [xml, drawer, cliFormOpen, pianoOpen, doc]); // eslint-disable-line react-hooks/exhaustive-deps

  // ------------------------------------------------------------------
  // derivati per il render (calcolati QUI, prima dei return condizionali:
  // l'effect del netto sotto deve girare a OGNI render — regole hook React)
  // ------------------------------------------------------------------
  const clienteSel = (fatt && fatt.clienteId) ? clienti.filter((c) => c.id === fatt.clienteId)[0] : null;
  // anteprima live SOLO sullo stato dirty; a bozza salvata fanno fede i numeri
  // del server (che vivono già dentro fatt dopo applyServer)
  const isDirty = !!fatt && (dirtyRef.current.testata || dirtyRef.current.righe
    || save.kind === 'error' || save.kind === 'conflict' || save.kind === 'saving');
  const live = (fatt && isDirty) ? anteprima(serializeForCalc(fatt), clienteSel) : null;
  const tot = live || fatt || {};

  // sync importo rata ← netto a pagare (finché l'utente non lo tocca, e SOLO
  // con una rata singola: un piano a tranche non si sovrascrive mai da solo)
  const nettoShown = fatt ? (tot.nettoAPagare || 0) : null;
  React.useEffect(() => {
    if (nettoShown == null) return;
    if (importoToccatoRef.current) return;
    const f = fattRef.current;
    if (!f || !f.pagamento || !f.pagamento.rate || f.pagamento.rate.length !== 1) return;
    const cur = f.pagamento.rate[0].importo;
    if (cur === nettoShown) return;
    // BOZZA ON-DEMAND: null→0 non è una modifica reale — senza questa guardia
    // l'apertura di 'Nuova fattura' sporcava la testata e creava la bozza
    // fantasma sul server (POST dall'autosave senza alcuna azione utente)
    if (cur == null && nettoShown === 0) return;
    dirtyRef.current.testata = true;
    setFatt((x) => {
      const p = x.pagamento || buildPagamentoVuoto();
      const rate = (p.rate || [buildRataVuota()]).slice();
      rate[0] = Object.assign({}, rate[0], { importo: nettoShown });
      return Object.assign({}, x, { pagamento: Object.assign({}, p, { rate }) });
    });
    deb();
  }, [nettoShown]); // eslint-disable-line react-hooks/exhaustive-deps

  // ------------------------------------------------------------------
  // stati non-felici del load
  // ------------------------------------------------------------------
  if (loadState === 'loading') {
    return <div className="f1-state">Caricamento bozza…</div>;
  }
  if (loadState === 'error') {
    return (
      <div className="f1-state f1-state-err">
        <div>⚠ {loadErr}</div>
        <button type="button" className="f1-btn" style={{ marginTop: 10 }} onClick={load}>Riprova</button>
      </div>
    );
  }
  if (!fatt) return null;

  // ------------------------------------------------------------------
  // dati derivati per il render (fatt qui è certamente non-null)
  // ------------------------------------------------------------------
  const isPa = !!(clienteSel && clienteSel.isPa);
  const split = !!(clienteSel && clienteSel.splitPayment);
  const dg = fatt.datiGenerali || {};
  const integr = tdIntegrazione(fatt.tipoDocumento);
  const ruoliInv = tdRuoliInvertiti(fatt.tipoDocumento);

  const clientiFiltrati = clienti.filter((c) => {
    if (!cliQuery) return true;
    const q = cliQuery.toLowerCase();
    return (nomeCliente(c).toLowerCase().indexOf(q) !== -1)
      || (c.partitaIva || '').indexOf(cliQuery) !== -1;
  });

  const errori = (problemi || []).filter((p) => p.livello === 'errore');
  const avvisi = (problemi || []).filter((p) => p.livello !== 'errore');

  const titolo = fatt.numeroCustom || fatt.numero || null;
  const cigCup = (dg.datiOrdine && dg.datiOrdine[0]) || null;
  function setCigCup(campo, val) {
    patchDGFn('datiOrdine', (arr) => {
      const a = (arr && arr.length) ? arr.slice() : [buildDocRifVuoto()];
      a[0] = Object.assign({}, a[0], { [campo]: val });
      return a;
    });
  }

  const rateArr = (fatt.pagamento && fatt.pagamento.rate && fatt.pagamento.rate.length)
    ? fatt.pagamento.rate : [buildRataVuota()];
  const sommaRateVal = sommaRate(rateArr);
  // IBAN unico dell'incasso: nel modello sta su ogni rata (lo pretende il tracciato),
  // in UI e' un campo solo — mostra il primo valorizzato e scrive su TUTTE le rate.
  const ibanComune = (rateArr.find((rt) => rt && rt.iban) || {}).iban || '';
  function setIbanTutteLeRate(v) {
    patchPagamento((p) => {
      const rate = ((p && p.rate && p.rate.length) ? p.rate : [buildRataVuota()])
        .map((rt) => Object.assign({}, rt, { iban: v }));
      return Object.assign({}, p, { rate });
    });
  }
  const nettoCorrente = tot.nettoAPagare || 0;
  const sommaCoincide = Math.round(sommaRateVal * 100) === Math.round(nettoCorrente * 100);
  const sommaDiff = (Math.round(sommaRateVal * 100) - Math.round(nettoCorrente * 100)) / 100;

  function toggleSez(id) { setOpen((o) => Object.assign({}, o, { [id]: !o[id] })); }

  // indicatore di salvataggio (risposta visibile a OGNI scrittura)
  let indicatore = null;
  if (save.kind === 'saving') indicatore = <span className="f1-ind saving">Salvataggio…</span>;
  else if (save.kind === 'saved') indicatore = <span className="f1-ind ok">{'Salvato alle ' + fmtOra(save.at)}</span>;
  else if (save.kind === 'error') indicatore = <span className="f1-ind err">ERRORE salvataggio</span>;
  else if (save.kind === 'conflict') indicatore = <span className="f1-ind err">Conflitto di versione</span>;
  else indicatore = <span className="f1-ind idle">{fatt.id ? 'Bozza salvata' : 'Nuova bozza — si salva alla prima modifica'}</span>;

  // ------------------------------------------------------------------
  // render
  // ------------------------------------------------------------------
  return (
    <div className="f1-editor-wrap" onBlur={onFieldBlur}>
      {/* barra azioni */}
      <div className="f1-ed-bar">
        <button type="button" className="f1-btn gray sm" onClick={chiudi}>‹ Chiudi</button>
        <div className="f1-ed-title">
          {titolo ? ('Fattura ' + titolo) : 'Bozza'}
          <span className="f1-badge-stato bozza">{fatt.stato === 'bozza' ? 'Bozza' : fatt.stato}</span>
        </div>
        {indicatore}
        <div className="f1-ed-actions">
          <button type="button" className="f1-btn sm" disabled={save.kind === 'saving'} onClick={() => flushSave()}>Salva</button>
          <button type="button" className="f1-btn ghost sm" disabled={validando} onClick={valida}>{validando ? 'Valido…' : 'Valida'}</button>
          <button type="button" className="f1-btn ghost sm" onClick={anteprimaXml}>Anteprima XML</button>
          <button type="button" className="f1-btn ghost sm" disabled={pdfBusy} onClick={anteprimaPdf}>{pdfBusy ? 'PDF…' : 'Anteprima PDF'}</button>
          <button type="button" className="f1-btn ghost sm" disabled={contBusy}
            title="Porta questa fattura in Contabilità → Fatture (tag OpenAPI)"
            onClick={registraInContabilita}>{contBusy ? 'Registro…' : 'Registra in Contabilità'}</button>
          <button type="button" className="f1-btn sm" disabled={inviando}
            title="Trasmette la fattura allo SDI tramite OpenAPI"
            onClick={invia}>{inviando ? 'Invio…' : 'Invia'}</button>
        </div>
      </div>

      {/* banner errore salvataggio: persistente, i dati restano nel form */}
      {save.kind === 'error' && (
        <div className="f1-banner err" role="alert">
          <strong>ERRORE salvataggio — i dati NON sono stati salvati.</strong>
          {' '}{save.msg || ''} I dati restano in questo form: riprova.
          <button type="button" className="f1-btn sm" style={{ marginLeft: 10 }} onClick={() => doSaveRef.current()}>Riprova ora</button>
        </div>
      )}

      {/* banner conflitto 409: BLOCCANTE, autosave sospeso */}
      {save.kind === 'conflict' && (
        <div className="f1-banner conflict" role="alert">
          <strong>Bozza modificata da un&rsquo;altra postazione.</strong>
          {' '}Il salvataggio automatico è sospeso finché non scegli:
          <div className="f1-conflict-actions">
            <button type="button" className="f1-btn sm" onClick={ricaricaVersione}>Ricarica la versione più recente</button>
            <button type="button" className="f1-btn ghost sm" onClick={continuaMiaCopia}>Continua sulla mia copia (sovrascrive)</button>
          </div>
        </div>
      )}

      {/* pannello problemi di validazione (cliccabili) */}
      {problemi != null && (
        <div className="f1-problemi">
          <div className="f1-problemi-h">
            <span>
              {problemi.length === 0
                ? 'Nessun problema: la bozza passa la validazione locale ✔'
                : errori.length + ' errori · ' + avvisi.length + ' avvisi (clicca per andare al campo)'}
            </span>
            <button type="button" className="f1-x" title="Chiudi" onClick={() => setProblemi(null)}>✕</button>
          </div>
          {errori.concat(avvisi).map((p, i) => (
            <button key={i} type="button" className={'f1-prob ' + (p.livello === 'errore' ? 'err' : 'warn')} onClick={() => focusProblema(p)}>
              <span className="f1-prob-cod">{p.codice}</span> {p.messaggio}
              <span className="f1-prob-campo">{p.campo}</span>
            </button>
          ))}
        </div>
      )}

      <div className="f1-editor">
        {/* ============ colonna FORM ============ */}
        <div className="f1-col-form">

          {/* ---- CLIENTE + DOCUMENTO affiancati ---- */}
          <div className="f1-duo">
          <div className="f1-card" id="f1-sez-cliente">
            <div className="f1-card-t">Cliente</div>
            <div className="f1-cli-row">
              <div className="f1-combo">
                <input
                  className="f1-in"
                  data-campo="clienteId"
                  placeholder={clienti.length ? 'Cerca cliente per nome o P.IVA…' : 'Nessun cliente: creane uno con “Nuovo cliente”'}
                  value={cliOpen ? cliQuery : (clienteSel ? nomeCliente(clienteSel) : (fatt.cessionario ? nomeCliente(fatt.cessionario) : ''))}
                  onFocus={() => { setCliOpen(true); setCliQuery(''); }}
                  onChange={(e) => setCliQuery(e.target.value)}
                  onBlur={() => win.setTimeout(() => { if (alive.current) setCliOpen(false); }, 180)}
                />
                {cliOpen && (
                  <div className="f1-combo-list">
                    {clientiFiltrati.length === 0 && <div className="f1-muted" style={{ padding: 8 }}>Nessun cliente trovato.</div>}
                    {clientiFiltrati.slice(0, 40).map((c) => (
                      <button type="button" key={c.id} className="f1-combo-item"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          patchTestata('clienteId', c.id);
                          patchTestata('formato', c.isPa ? 'FPA12' : 'FPR12');
                          setCliOpen(false);
                        }}>
                        <span>{nomeCliente(c)}</span>
                        <span className="f1-muted">{c.partitaIva || c.codiceFiscale || ''}{c.isPa ? ' · PA' : ''}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button type="button" className="f1-btn ghost sm" onClick={() => setCliFormOpen(true)}>+ Nuovo cliente</button>
            </div>
            {clienteSel && (
              <div className="f1-cli-info">
                <span>P.IVA: <b>{clienteSel.partitaIva || '—'}</b></span>
                <span>SDI: <b>{clienteSel.codiceDestinatario || (clienteSel.pec ? 'PEC: ' + clienteSel.pec : '0000000')}</b></span>
                {clienteSel.splitPayment && <span className="f1-pill">split payment</span>}
              </div>
            )}
            {isPa && (
              <div className="f1-banner pa">
                <strong>Cliente PA — formato FPA12.</strong> Firma qualificata obbligatoria all&rsquo;invio; CIG/CUP servono per il pagamento.
                <div className="f1-grid" style={{ marginTop: 8 }}>
                  <Campo label="CIG">
                    <input className="f1-in" data-campo="datiGenerali.datiOrdine[0].cig" maxLength={15}
                      value={(cigCup && cigCup.cig) || ''} onChange={(e) => setCigCup('cig', e.target.value)} />
                  </Campo>
                  <Campo label="CUP">
                    <input className="f1-in" data-campo="datiGenerali.datiOrdine[0].cup" maxLength={15}
                      value={(cigCup && cigCup.cup) || ''} onChange={(e) => setCigCup('cup', e.target.value)} />
                  </Campo>
                </div>
              </div>
            )}
          </div>

          {/* ---- DOCUMENTO ---- */}
          <div className="f1-card" id="f1-sez-documento">
            <div className="f1-card-t">Documento</div>
            <div className="f1-grid">
              <Campo label="Tipo documento" wide>
                <select className="f1-in" data-campo="tipoDocumento" value={fatt.tipoDocumento}
                  onChange={(e) => patchTestata('tipoDocumento', e.target.value)}>
                  <optgroup label="Documenti">
                    {TIPI_DOCUMENTO.filter((t) => TD_BASE.indexOf(t.code) !== -1).map((t) => (
                      <option key={t.code} value={t.code}>{t.code + ' — ' + t.label}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Integrazioni / Autofatture (TD16-TD29)">
                    {TIPI_DOCUMENTO.filter((t) => TD_BASE.indexOf(t.code) === -1).map((t) => (
                      <option key={t.code} value={t.code}>{t.code + ' — ' + t.label}</option>
                    ))}
                  </optgroup>
                </select>
              </Campo>
              {integr && (
                <div className="f1-banner warn wide">
                  <strong>Integrazione/autofattura:</strong> percorso guidato in arrivo — ruoli invertiti.
                  <label className="f1-check" style={{ marginTop: 6 }}>
                    <input type="checkbox" checked={ruoliInv} disabled readOnly />
                    <span>ruoli invertiti (derivato dal tipo documento, sola lettura)</span>
                  </label>
                </div>
              )}
              <Campo label="Data emissione">
                <input className="f1-in" type="date" data-campo="dataEmissione" value={fatt.dataEmissione || ''}
                  onChange={(e) => patchTestata('dataEmissione', e.target.value || oggiISO())} />
              </Campo>
              <Campo label="Serie" hint="vuota = numerazione unica">
                <input className="f1-in" data-campo="serie" value={fatt.serie || ''}
                  onChange={(e) => patchTestata('serie', e.target.value.trim())} placeholder="es. B" />
              </Campo>
              <Campo label="Numero" hint="prossimo numero previsto (definitivo all'invio)">
                <input className="f1-in" data-campo="numero" readOnly disabled
                  value={numeroPrev != null ? (String(numeroPrev) + (fatt.serie ? '/' + fatt.serie : '')) : '—'} />
              </Campo>
              <Campo label="Divisa" hint="modificabile nelle sezioni avanzate (Altro)">
                <input className="f1-in" readOnly disabled value={fatt.divisa || 'EUR'} />
              </Campo>
            </div>
          </div>
          </div>{/* /f1-duo */}

          {/* ---- RIGHE ---- */}
          <div className="f1-card" id="f1-sez-righe">
            <div className="f1-card-t">Righe</div>
            <datalist id="f1-voci-dl">
              {voci.map((v) => <option key={v.id} value={v.titolo} />)}
            </datalist>
            <div className="f1-righe-scroll">
              <table className="f1-righe">
                <thead>
                  <tr>
                    <th style={{ width: 26 }}>#</th>
                    <th>Descrizione</th>
                    <th style={{ width: 70 }}>Qtà</th>
                    <th style={{ width: 60 }}>U.M.</th>
                    <th style={{ width: 100 }}>Prezzo unit.</th>
                    <th style={{ width: 100 }}>Imponibile</th>
                    <th style={{ width: 95 }}>Importo IVA</th>
                    <th style={{ width: 110 }}>Totale</th>
                    <th style={{ width: 200 }}>IVA / Natura</th>
                    <th style={{ width: 118 }} />
                  </tr>
                </thead>
                <tbody>
                  {fatt.righe.map((r, i) => {
                    const last = i === fatt.righe.length - 1;
                    const rTot = (live && live.righe && live.righe[i]) ? live.righe[i].prezzoTotale : r.prezzoTotale;
                    // IVA della singola riga: indicativa (l'imposta ufficiale si arrotonda
                    // per riepilogo aliquota/natura, non per riga — scarto 00421)
                    const rIva = r.aliquotaIva ? Math.round((rTot || 0) * r.aliquotaIva) / 100 : 0;
                    return (
                      <React.Fragment key={r._key}>
                        <tr className="f1-riga">
                          <td className="f1-muted">{i + 1}</td>
                          <td>
                            <input className="f1-in" list="f1-voci-dl" data-campo={'righe[' + i + '].descrizione'}
                              placeholder="Descrizione (suggerimenti dalle Voci)"
                              value={r.descrizione} onChange={(e) => onDescrizione(i, e.target.value)} />
                          </td>
                          <td>
                            <input className="f1-in num" type="number" step="any" min="0" data-campo={'righe[' + i + '].quantita'}
                              value={r.quantita == null ? '' : r.quantita}
                              onChange={(e) => patchRiga(i, 'quantita', numOrNull(e.target.value))} placeholder="1" />
                          </td>
                          <td>
                            <input className="f1-in" data-campo={'righe[' + i + '].unitaMisura'} value={r.unitaMisura || ''}
                              onChange={(e) => patchRiga(i, 'unitaMisura', e.target.value)} placeholder="pz" />
                          </td>
                          <td>
                            <input className="f1-in num" type="number" step="any" data-campo={'righe[' + i + '].prezzoUnitario'}
                              value={r.prezzoUnitario == null ? '' : r.prezzoUnitario}
                              onChange={(e) => patchRiga(i, 'prezzoUnitario', numOrNull(e.target.value) || 0)} />
                          </td>
                          <td className="f1-num-cell">{fmtEuro(rTot)}</td>
                          <td className="f1-num-cell f1-iva-imp">{fmtEuro(rIva)}</td>
                          <td className="f1-num-cell"><b>{fmtEuro(rTot + rIva)}</b></td>
                          <td className="f1-iva-cell">
                            <select className={'f1-in' + (r.aliquotaIva === 0 && !r.natura ? ' f1-in-warn' : '')}
                              data-campo={'righe[' + i + '].aliquotaIva'} value={ivaPresetValue(r.aliquotaIva, r.natura)}
                              onChange={(e) => {
                                const p = ivaPresetParse(e.target.value);
                                patchRighe((righe) => {
                                  const a = righe.slice();
                                  a[i] = Object.assign({}, a[i], { aliquotaIva: p.aliquotaIva, natura: p.natura });
                                  return a;
                                });
                              }}
                              onKeyDown={(e) => onUltimaCellaKey(e, last)}>
                              {r.aliquotaIva === 0 && !r.natura && <option value="0|">0% — scegli la natura…</option>}
                              {IVA_PRESETS.map((g) => (
                                <optgroup key={g.gruppo} label={g.gruppo}>
                                  {g.voci.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
                                </optgroup>
                              ))}
                            </select>
                          </td>
                          <td className="f1-riga-btns">
                            <button type="button" className="f1-ico" title="Sposta su" disabled={i === 0} onClick={() => moveRiga(i, -1)}>↑</button>
                            <button type="button" className="f1-ico" title="Sposta giù" disabled={last} onClick={() => moveRiga(i, 1)}>↓</button>
                            <button type="button" className={'f1-ico' + (rigaAvanzataCompilata(r) ? ' comp' : '')}
                              title="Avanzate riga (sconti, codici articolo, periodo…)" onClick={() => setDrawer(i)}>⋯</button>
                            <button type="button" className="f1-del" title="Elimina riga" onClick={() => delRiga(i)}>✕</button>
                          </td>
                        </tr>
                        {/* la natura si sceglie DIRETTAMENTE nel selettore IVA (preset
                            aliquota+natura): niente seconda riga che si ripete — collaudo
                            utente 2026-07-20 */}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <button type="button" className="f1-btn gray sm" style={{ marginTop: 8 }} onClick={addRiga}>+ Aggiungi riga</button>
            <span className="f1-hint" style={{ marginLeft: 10 }}>Invio o Tab sull&rsquo;ultima cella aggiunge una riga</span>
          </div>

          {/* ---- PAGAMENTO (rate multiple / piano a tranche) ---- */}
          <div className="f1-card" id="f1-sez-pagamento">
            <div className="f1-card-t">Pagamento</div>
            <div className="f1-grid">
              <Campo label="Condizioni" hint={rateArr.length > 1 ? 'con più rate: TP01 (impostato automaticamente)' : null}>
                <select className="f1-in" data-campo="pagamento.condizioni" value={(fatt.pagamento && fatt.pagamento.condizioni) || 'TP02'}
                  onChange={(e) => patchPagamento((p) => Object.assign({}, p, { condizioni: e.target.value }))}>
                  {CONDIZIONI_PAGAMENTO.map((c) => <option key={c.code} value={c.code}>{c.code + ' — ' + c.label}</option>)}
                </select>
              </Campo>
            </div>
            {/* IBAN UNICO per l'incasso: nel tracciato XML sta su ogni DettaglioPagamento,
                ma per l'utente e' sempre lo stesso conto — un campo solo, propagato a
                tutte le rate (collaudo utente 2026-07-20: "non ripeterlo 30 volte"). */}
            <div className="f1-grid" style={{ marginTop: 4 }}>
              <Campo label="IBAN di incasso" hint="vale per tutte le rate" wide
                error={ibanComune && !ibanValido(ibanComune) ? 'Formato IBAN non valido' : null}>
                <input className={'f1-in' + (ibanComune && !ibanValido(ibanComune) ? ' f1-in-warn' : '')}
                  data-campo="pagamento.rate[0].iban" value={ibanComune}
                  onChange={(e) => setIbanTutteLeRate(e.target.value.replace(/\s+/g, '').toUpperCase())}
                  placeholder="IT00X0000000000000000000000" />
              </Campo>
            </div>
            <div className="f1-sublist-t">{rateArr.length === 1 ? 'Rata' : 'Rate (' + rateArr.length + ')'}</div>
            <div className="f1-righe-scroll">
              <div className="f1-rate-min">
                <div className="f1-rate-grid">
                  <span />
                  <span className="f1-rate-h">Modalità</span>
                  <span className="f1-rate-h">Scadenza</span>
                  <span className="f1-rate-h">Importo</span>
                  <span />
                </div>
                {rateArr.map((rt, i) => (
                  <React.Fragment key={i}>
                    <div className="f1-rate-grid">
                      <span className="f1-muted">{i + 1}</span>
                      <select className="f1-in" data-campo={'pagamento.rate[' + i + '].modalita'} value={rt.modalita || 'MP05'}
                        onChange={(e) => patchRata(i, 'modalita', e.target.value)}>
                        {MODALITA_PAGAMENTO.map((m) => <option key={m.code} value={m.code}>{m.code + ' — ' + m.label}</option>)}
                      </select>
                      <input className="f1-in" type="date" data-campo={'pagamento.rate[' + i + '].dataScadenza'} value={rt.dataScadenza || ''}
                        onChange={(e) => patchRata(i, 'dataScadenza', e.target.value || null)} />
                      <input className="f1-in num" type="number" step="any" data-campo={'pagamento.rate[' + i + '].importo'}
                        title={i === 0 && rateArr.length === 1 ? 'precompilato col netto a pagare' : 'importo della rata'}
                        value={rt.importo == null ? '' : rt.importo}
                        onChange={(e) => { importoToccatoRef.current = true; patchRata(i, 'importo', numOrNull(e.target.value)); }} />
                      <button type="button" className="f1-del" title={rateArr.length === 1 ? 'Almeno una rata deve restare' : 'Rimuovi rata'}
                        disabled={rateArr.length === 1} onClick={() => delRata(i)}>✕</button>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>
            <div className="f1-rate-actions">
              <button type="button" className="f1-btn gray sm" onClick={addRata}>+ Aggiungi rata</button>
              <button type="button" className="f1-btn ghost sm" onClick={() => setPianoOpen(true)}>Genera piano rate…</button>
            </div>
            <div className="f1-rate-somma">
              <span>Somma rate: <b>{fmtEuro(sommaRateVal)}</b></span>
              {sommaCoincide
                ? <span className="f1-pill ok">= netto a pagare</span>
                : <span className="f1-pill">{'differenza ' + fmtEuro(sommaDiff) + ' sul netto ' + fmtEuro(nettoCorrente)}</span>}
            </div>
          </div>

          {/* ============ SEZIONI AVANZATE ============ */}
          <div className="f1-avanzate-t">Sezioni avanzate</div>

          <Sezione id="ritenute" titolo="Ritenute" compilato={sezioneCompilata(fatt, 'ritenute')} open={open.ritenute} onToggle={toggleSez}>
            {(dg.ritenute || []).map((rt, i) => (
              <div className="f1-subrow" key={i}>
                <select className="f1-in" style={{ flex: 2 }} data-campo={'datiGenerali.ritenute[' + i + '].tipo'} value={rt.tipo || 'RT01'}
                  onChange={(e) => patchDGFn('ritenute', (a) => { const b = a.slice(); b[i] = Object.assign({}, b[i], { tipo: e.target.value }); return b; })}>
                  {TIPI_RITENUTA.map((t) => <option key={t.code} value={t.code}>{t.code + ' — ' + t.label}</option>)}
                </select>
                <input className="f1-in num" style={{ flex: 1 }} type="number" step="any" placeholder="Aliquota %"
                  value={rt.aliquota == null ? '' : rt.aliquota}
                  onChange={(e) => patchDGFn('ritenute', (a) => { const b = a.slice(); b[i] = Object.assign({}, b[i], { aliquota: numOrNull(e.target.value) }); return b; })} />
                <input className="f1-in num" style={{ flex: 1 }} type="number" step="any" placeholder="Importo"
                  data-campo={'datiGenerali.ritenute[' + i + '].importo'}
                  value={rt.importo == null ? '' : rt.importo}
                  onChange={(e) => patchDGFn('ritenute', (a) => { const b = a.slice(); b[i] = Object.assign({}, b[i], { importo: numOrNull(e.target.value) }); return b; })} />
                <select className="f1-in" style={{ flex: 1 }} title="Causale pagamento (CU)"
                  data-campo={'datiGenerali.ritenute[' + i + '].causale'} value={rt.causale || 'A'}
                  onChange={(e) => patchDGFn('ritenute', (a) => { const b = a.slice(); b[i] = Object.assign({}, b[i], { causale: e.target.value }); return b; })}>
                  {CAUSALI_PAGAMENTO.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <button type="button" className="f1-del" title="Rimuovi" onClick={() => patchDGFn('ritenute', (a) => a.filter((x, j) => j !== i))}>✕</button>
              </div>
            ))}
            <button type="button" className="f1-btn gray sm" onClick={addRitenuta}>+ Aggiungi ritenuta</button>
            <div className="f1-hint">L&rsquo;importo va indicato: il flag ritenuta sulle righe si gestisce dal drawer «⋯» di ogni riga.</div>
          </Sezione>

          <Sezione id="cassa" titolo="Cassa previdenziale" compilato={sezioneCompilata(fatt, 'cassa')} open={open.cassa} onToggle={toggleSez}>
            {(dg.casse || []).map((cs, i) => (
              <div className="f1-subgrid" key={i}>
                <select className="f1-in" data-campo={'datiGenerali.casse[' + i + '].tipo'} value={cs.tipo || 'TC01'}
                  onChange={(e) => patchDGFn('casse', (a) => { const b = a.slice(); b[i] = Object.assign({}, b[i], { tipo: e.target.value }); return b; })}>
                  {TIPI_CASSA.map((t) => <option key={t.code} value={t.code}>{t.code + ' — ' + t.label}</option>)}
                </select>
                <input className="f1-in num" type="number" step="any" placeholder="% cassa" value={cs.alCassa == null ? '' : cs.alCassa}
                  onChange={(e) => patchDGFn('casse', (a) => { const b = a.slice(); b[i] = Object.assign({}, b[i], { alCassa: numOrNull(e.target.value) }); return b; })} />
                <input className="f1-in num" type="number" step="any" placeholder="Importo" value={cs.importo == null ? '' : cs.importo}
                  onChange={(e) => patchDGFn('casse', (a) => { const b = a.slice(); b[i] = Object.assign({}, b[i], { importo: numOrNull(e.target.value) }); return b; })} />
                <input className="f1-in num" type="number" step="any" placeholder="Imponibile" value={cs.imponibile == null ? '' : cs.imponibile}
                  onChange={(e) => patchDGFn('casse', (a) => { const b = a.slice(); b[i] = Object.assign({}, b[i], { imponibile: numOrNull(e.target.value) }); return b; })} />
                <input className="f1-in num" type="number" step="any" placeholder="IVA %" value={cs.aliquotaIva == null ? '' : cs.aliquotaIva}
                  onChange={(e) => patchDGFn('casse', (a) => { const b = a.slice(); b[i] = Object.assign({}, b[i], { aliquotaIva: numOrNull(e.target.value) }); return b; })} />
                <select className="f1-in" value={cs.natura || ''} title="Natura (se IVA 0)"
                  onChange={(e) => patchDGFn('casse', (a) => { const b = a.slice(); b[i] = Object.assign({}, b[i], { natura: e.target.value }); return b; })}>
                  <option value="">— natura —</option>
                  {NATURE.map((n) => <option key={n.code} value={n.code}>{n.code}</option>)}
                </select>
                <label className="f1-check">
                  <input type="checkbox" checked={!!cs.ritenuta}
                    onChange={(e) => patchDGFn('casse', (a) => { const b = a.slice(); b[i] = Object.assign({}, b[i], { ritenuta: e.target.checked }); return b; })} />
                  <span>soggetta a ritenuta</span>
                </label>
                <button type="button" className="f1-del" title="Rimuovi" onClick={() => patchDGFn('casse', (a) => a.filter((x, j) => j !== i))}>✕</button>
              </div>
            ))}
            <button type="button" className="f1-btn gray sm"
              onClick={() => patchDGFn('casse', (a) => (a || []).concat([{ tipo: 'TC04', alCassa: 4, importo: null, imponibile: null, aliquotaIva: 22, ritenuta: false, natura: '', riferimentoAmministrazione: '' }]))}>
              + Aggiungi cassa
            </button>
          </Sezione>

          <Sezione id="bollo" titolo="Bollo (override)" compilato={sezioneCompilata(fatt, 'bollo')} open={open.bollo} onToggle={toggleSez}>
            <label className="f1-check">
              <input type="checkbox" data-campo="datiGenerali.bollo" checked={!!dg.bollo}
                onChange={(e) => patchDG('bollo', e.target.checked ? { virtuale: true, importo: 2.00 } : null)} />
              <span>Bollo virtuale assolto</span>
            </label>
            {dg.bollo && (
              <Campo label="Importo bollo">
                <input className="f1-in num" type="number" step="any" value={dg.bollo.importo == null ? '' : dg.bollo.importo}
                  onChange={(e) => patchDG('bollo', Object.assign({}, dg.bollo, { importo: numOrNull(e.target.value) }))} />
              </Campo>
            )}
            <div className="f1-hint">Il motore propone il bollo quando le nature soggette superano 77,47 € (vedi riepilogo).</div>
          </Sezione>

          <Sezione id="sconti" titolo="Sconti / maggiorazioni documento" compilato={sezioneCompilata(fatt, 'sconti')} open={open.sconti} onToggle={toggleSez}>
            {(dg.scontiDoc || []).map((s, i) => (
              <div className="f1-subrow" key={i}>
                <select className="f1-in" style={{ flex: 1 }} data-campo={'datiGenerali.scontiDoc[' + i + '].tipo'} value={s.tipo || 'SC'}
                  onChange={(e) => patchDGFn('scontiDoc', (a) => { const b = a.slice(); b[i] = Object.assign({}, b[i], { tipo: e.target.value }); return b; })}>
                  <option value="SC">Sconto</option>
                  <option value="MG">Maggiorazione</option>
                </select>
                <input className="f1-in num" style={{ flex: 1 }} type="number" step="any" placeholder="%" value={s.percentuale == null ? '' : s.percentuale}
                  onChange={(e) => patchDGFn('scontiDoc', (a) => { const b = a.slice(); b[i] = Object.assign({}, b[i], { percentuale: numOrNull(e.target.value) }); return b; })} />
                <input className="f1-in num" style={{ flex: 1 }} type="number" step="any" placeholder="Importo" value={s.importo == null ? '' : s.importo}
                  onChange={(e) => patchDGFn('scontiDoc', (a) => { const b = a.slice(); b[i] = Object.assign({}, b[i], { importo: numOrNull(e.target.value) }); return b; })} />
                <button type="button" className="f1-del" title="Rimuovi" onClick={() => patchDGFn('scontiDoc', (a) => a.filter((x, j) => j !== i))}>✕</button>
              </div>
            ))}
            <button type="button" className="f1-btn gray sm" onClick={() => patchDGFn('scontiDoc', (a) => (a || []).concat([{ tipo: 'SC', percentuale: null, importo: null }]))}>+ Aggiungi</button>
            <div className="f1-grid" style={{ marginTop: 10 }}>
              <Campo label="Importo totale documento" hint="facoltativo (2.1.1.9)">
                <input className="f1-in num" type="number" step="any" data-campo="datiGenerali.importoTotale"
                  value={dg.importoTotale == null ? '' : dg.importoTotale}
                  onChange={(e) => patchDG('importoTotale', numOrNull(e.target.value))} />
              </Campo>
              <Campo label="Arrotondamento">
                <input className="f1-in num" type="number" step="any" data-campo="datiGenerali.arrotondamento"
                  value={dg.arrotondamento == null ? '' : dg.arrotondamento}
                  onChange={(e) => patchDG('arrotondamento', numOrNull(e.target.value))} />
              </Campo>
            </div>
          </Sezione>

          <Sezione id="causali" titolo="Causali" compilato={sezioneCompilata(fatt, 'causali')} open={open.causali} onToggle={toggleSez}>
            {(dg.causali || []).map((c, i) => (
              <div className="f1-subrow" key={i}>
                <textarea className="f1-in" rows={2} maxLength={200} style={{ flex: 1 }} data-campo={'datiGenerali.causali[' + i + ']'}
                  value={c || ''} placeholder="Causale (max 200 caratteri)"
                  onChange={(e) => patchDGFn('causali', (a) => { const b = a.slice(); b[i] = e.target.value; return b; })} />
                <button type="button" className="f1-del" title="Rimuovi" onClick={() => patchDGFn('causali', (a) => a.filter((x, j) => j !== i))}>✕</button>
              </div>
            ))}
            <button type="button" className="f1-btn gray sm" onClick={() => patchDGFn('causali', (a) => (a || []).concat(['']))}>+ Aggiungi causale</button>
          </Sezione>

          <Sezione id="altro" titolo="Altro documento (Art. 73, divisa)" compilato={sezioneCompilata(fatt, 'altro')} open={open.altro} onToggle={toggleSez}>
            <label className="f1-check">
              <input type="checkbox" data-campo="art73" checked={!!fatt.art73} onChange={(e) => patchTestata('art73', e.target.checked)} />
              <span>Art. 73 DPR 633/72 (emissione con numerazione propria / duplicati ammessi nell&rsquo;anno)</span>
            </label>
            <Campo label="Divisa (ISO 4217)" hint="di norma EUR">
              <input className="f1-in" data-campo="divisa" maxLength={3} value={fatt.divisa || 'EUR'}
                onChange={(e) => patchTestata('divisa', e.target.value.toUpperCase())} />
            </Campo>
          </Sezione>

          <Sezione id="doccollegati" titolo="Documenti collegati (ordine, contratto, convenzione, ricezione, fatture)" compilato={sezioneCompilata(fatt, 'doccollegati')} open={open.doccollegati} onToggle={toggleSez}>
            <DocRifList titolo="Ordini d'acquisto" list={dg.datiOrdine} pathBase="datiGenerali.datiOrdine" onChange={(a) => patchDG('datiOrdine', a)} />
            <DocRifList titolo="Contratti" list={dg.datiContratto} pathBase="datiGenerali.datiContratto" onChange={(a) => patchDG('datiContratto', a)} />
            <DocRifList titolo="Convenzioni" list={dg.datiConvenzione} pathBase="datiGenerali.datiConvenzione" onChange={(a) => patchDG('datiConvenzione', a)} />
            <DocRifList titolo="Dati ricezione" list={dg.datiRicezione} pathBase="datiGenerali.datiRicezione" onChange={(a) => patchDG('datiRicezione', a)} />
            <DocRifList titolo="Fatture collegate" list={dg.fattureCollegate} pathBase="datiGenerali.fattureCollegate" onChange={(a) => patchDG('fattureCollegate', a)} />
          </Sezione>

          <Sezione id="ddt" titolo="DDT" compilato={sezioneCompilata(fatt, 'ddt')} open={open.ddt} onToggle={toggleSez}>
            {(dg.datiDdt || []).map((d, i) => (
              <div className="f1-subrow" key={i}>
                <input className="f1-in" style={{ flex: 2 }} placeholder="Numero DDT" data-campo={'datiGenerali.datiDdt[' + i + '].numero'}
                  value={d.numero || ''} onChange={(e) => patchDGFn('datiDdt', (a) => { const b = a.slice(); b[i] = Object.assign({}, b[i], { numero: e.target.value }); return b; })} />
                <input className="f1-in" type="date" style={{ flex: 1 }} value={d.data || ''}
                  onChange={(e) => patchDGFn('datiDdt', (a) => { const b = a.slice(); b[i] = Object.assign({}, b[i], { data: e.target.value || null }); return b; })} />
                <input className="f1-in" style={{ flex: 1 }} placeholder="Righe (es. 1,2)" value={joinIntList(d.riferimentoLinee)}
                  onChange={(e) => patchDGFn('datiDdt', (a) => { const b = a.slice(); b[i] = Object.assign({}, b[i], { riferimentoLinee: parseIntList(e.target.value) }); return b; })} />
                <button type="button" className="f1-del" title="Rimuovi" onClick={() => patchDGFn('datiDdt', (a) => a.filter((x, j) => j !== i))}>✕</button>
              </div>
            ))}
            <button type="button" className="f1-btn gray sm" onClick={() => patchDGFn('datiDdt', (a) => (a || []).concat([{ numero: '', data: null, riferimentoLinee: [] }]))}>+ Aggiungi DDT</button>
          </Sezione>

          <Sezione id="trasporto" titolo="Trasporto" compilato={sezioneCompilata(fatt, 'trasporto')} open={open.trasporto} onToggle={toggleSez}>
            <label className="f1-check">
              <input type="checkbox" data-campo="datiGenerali.datiTrasporto" checked={!!dg.datiTrasporto}
                onChange={(e) => {
                  if (!e.target.checked && dg.datiTrasporto && !win.confirm('Rimuovere i dati di trasporto inseriti?')) return;
                  patchDG('datiTrasporto', e.target.checked ? {
                    vettore: { idPaese: 'IT', idCodice: '', codiceFiscale: '', denominazione: '', numeroLicenzaGuida: '' },
                    mezzo: '', causale: '', numeroColli: null, descrizione: '', unitaMisuraPeso: 'KG',
                    pesoLordo: null, pesoNetto: null, dataOraRitiro: null, dataInizioTrasporto: null,
                    tipoResa: '', indirizzoResa: null, dataOraConsegna: null,
                  } : null);
                }} />
              <span>Includi dati trasporto</span>
            </label>
            {dg.datiTrasporto && (
              <div className="f1-grid" style={{ marginTop: 8 }}>
                <Campo label="Vettore — denominazione">
                  <input className="f1-in" value={(dg.datiTrasporto.vettore && dg.datiTrasporto.vettore.denominazione) || ''}
                    onChange={(e) => patchDG('datiTrasporto', Object.assign({}, dg.datiTrasporto, { vettore: Object.assign({}, dg.datiTrasporto.vettore, { denominazione: e.target.value }) }))} />
                </Campo>
                <Campo label="Vettore — P.IVA">
                  <input className="f1-in" value={(dg.datiTrasporto.vettore && dg.datiTrasporto.vettore.idCodice) || ''}
                    onChange={(e) => patchDG('datiTrasporto', Object.assign({}, dg.datiTrasporto, { vettore: Object.assign({}, dg.datiTrasporto.vettore, { idCodice: e.target.value.trim() }) }))} />
                </Campo>
                <Campo label="Mezzo">
                  <input className="f1-in" value={dg.datiTrasporto.mezzo || ''}
                    onChange={(e) => patchDG('datiTrasporto', Object.assign({}, dg.datiTrasporto, { mezzo: e.target.value }))} />
                </Campo>
                <Campo label="Causale trasporto">
                  <input className="f1-in" value={dg.datiTrasporto.causale || ''}
                    onChange={(e) => patchDG('datiTrasporto', Object.assign({}, dg.datiTrasporto, { causale: e.target.value }))} />
                </Campo>
                <Campo label="Numero colli">
                  <input className="f1-in num" type="number" value={dg.datiTrasporto.numeroColli == null ? '' : dg.datiTrasporto.numeroColli}
                    onChange={(e) => patchDG('datiTrasporto', Object.assign({}, dg.datiTrasporto, { numeroColli: numOrNull(e.target.value) }))} />
                </Campo>
                <Campo label="Descrizione">
                  <input className="f1-in" value={dg.datiTrasporto.descrizione || ''}
                    onChange={(e) => patchDG('datiTrasporto', Object.assign({}, dg.datiTrasporto, { descrizione: e.target.value }))} />
                </Campo>
                <Campo label="Peso lordo">
                  <input className="f1-in num" type="number" step="any" value={dg.datiTrasporto.pesoLordo == null ? '' : dg.datiTrasporto.pesoLordo}
                    onChange={(e) => patchDG('datiTrasporto', Object.assign({}, dg.datiTrasporto, { pesoLordo: numOrNull(e.target.value) }))} />
                </Campo>
                <Campo label="Peso netto">
                  <input className="f1-in num" type="number" step="any" value={dg.datiTrasporto.pesoNetto == null ? '' : dg.datiTrasporto.pesoNetto}
                    onChange={(e) => patchDG('datiTrasporto', Object.assign({}, dg.datiTrasporto, { pesoNetto: numOrNull(e.target.value) }))} />
                </Campo>
                <Campo label="Inizio trasporto">
                  <input className="f1-in" type="date" value={dg.datiTrasporto.dataInizioTrasporto || ''}
                    onChange={(e) => patchDG('datiTrasporto', Object.assign({}, dg.datiTrasporto, { dataInizioTrasporto: e.target.value || null }))} />
                </Campo>
                <Campo label="Consegna (data e ora)">
                  <input className="f1-in" type="datetime-local" value={dg.datiTrasporto.dataOraConsegna || ''}
                    onChange={(e) => patchDG('datiTrasporto', Object.assign({}, dg.datiTrasporto, { dataOraConsegna: e.target.value || null }))} />
                </Campo>
                <Campo label="Tipo resa (Incoterms)">
                  <input className="f1-in" maxLength={3} value={dg.datiTrasporto.tipoResa || ''}
                    onChange={(e) => patchDG('datiTrasporto', Object.assign({}, dg.datiTrasporto, { tipoResa: e.target.value.toUpperCase() }))} placeholder="es. EXW" />
                </Campo>
              </div>
            )}
          </Sezione>

          <Sezione id="fattprincipale" titolo="Fattura principale" compilato={sezioneCompilata(fatt, 'fattprincipale')} open={open.fattprincipale} onToggle={toggleSez}>
            <div className="f1-grid">
              <Campo label="Numero">
                <input className="f1-in" data-campo="datiGenerali.fatturaPrincipale.numero"
                  value={(dg.fatturaPrincipale && dg.fatturaPrincipale.numero) || ''}
                  onChange={(e) => patchDG('fatturaPrincipale', e.target.value || (dg.fatturaPrincipale && dg.fatturaPrincipale.data)
                    ? Object.assign({}, dg.fatturaPrincipale || {}, { numero: e.target.value }) : null)} />
              </Campo>
              <Campo label="Data">
                <input className="f1-in" type="date" value={(dg.fatturaPrincipale && dg.fatturaPrincipale.data) || ''}
                  onChange={(e) => patchDG('fatturaPrincipale', e.target.value || (dg.fatturaPrincipale && dg.fatturaPrincipale.numero)
                    ? Object.assign({}, dg.fatturaPrincipale || {}, { data: e.target.value || null }) : null)} />
              </Campo>
            </div>
          </Sezione>

          <Sezione id="veicoli" titolo="Veicoli (art. 38 c.4 DL 331/93)" compilato={sezioneCompilata(fatt, 'veicoli')} open={open.veicoli} onToggle={toggleSez}>
            <div className="f1-grid">
              <Campo label="Data">
                <input className="f1-in" type="date" data-campo="datiGenerali.datiVeicoli.data"
                  value={(dg.datiVeicoli && dg.datiVeicoli.data) || ''}
                  onChange={(e) => patchDG('datiVeicoli', e.target.value || (dg.datiVeicoli && dg.datiVeicoli.totalePercorso)
                    ? Object.assign({}, dg.datiVeicoli || {}, { data: e.target.value || null }) : null)} />
              </Campo>
              <Campo label="Totale percorso">
                <input className="f1-in" value={(dg.datiVeicoli && dg.datiVeicoli.totalePercorso) || ''}
                  onChange={(e) => patchDG('datiVeicoli', e.target.value || (dg.datiVeicoli && dg.datiVeicoli.data)
                    ? Object.assign({}, dg.datiVeicoli || {}, { totalePercorso: e.target.value }) : null)} placeholder="es. 1500 km" />
              </Campo>
            </div>
          </Sezione>
        </div>

        {/* ============ colonna RIEPILOGO (sticky) ============ */}
        <div className="f1-col-side">
          <div className="f1-card f1-sticky">
            <div className="f1-card-t">Riepilogo {isDirty && live ? <span className="f1-pill">anteprima</span> : <span className="f1-pill ok">dal server</span>}</div>
            <div className="f1-tot-row"><span>Imponibile</span><b>{fmtEuro(tot.totaleImponibile)}</b></div>
            {(tot.riepiloghi || []).map((r, i) => (
              <div className="f1-tot-row sub" key={i}>
                <span>{r.aliquotaIva > 0 ? ('IVA ' + r.aliquotaIva + '%') : ('Natura ' + (r.natura || '—'))}{r.esigibilita === 'S' ? ' (scissa)' : ''}</span>
                <span>{fmtEuro(r.imposta)}</span>
              </div>
            ))}
            <div className="f1-tot-row"><span>IVA totale</span><b>{fmtEuro(tot.totaleImposta)}</b></div>
            {dg.bollo && <div className="f1-tot-row sub"><span>Bollo</span><span>{fmtEuro(dg.bollo.importo)}</span></div>}
            <div className="f1-tot-row big"><span>Totale documento</span><b>{fmtEuro(tot.totaleDocumento)}</b></div>
            {(tot.totaleRitenute || 0) > 0 && (
              <div className="f1-tot-row"><span>− Ritenute</span><span>{fmtEuro(tot.totaleRitenute)}</span></div>
            )}
            {split && (
              <div className="f1-tot-row"><span>− IVA scissa (split payment)</span><span>{fmtEuro(tot.totaleImposta)}</span></div>
            )}
            <div className="f1-tot-row netto"><span>NETTO A PAGARE</span><b>{fmtEuro(tot.nettoAPagare)}</b></div>
            <div className="f1-bollo-box">
              <label className="f1-check">
                <input type="checkbox" checked={!!dg.bollo}
                  onChange={(e) => patchDG('bollo', e.target.checked ? { virtuale: true, importo: 2.00 } : null)} />
                <span>Bollo €2,00</span>
              </label>
              {tot.bolloProposto && !dg.bollo && <span className="f1-warn-inline">proposto: nature soggette &gt; 77,47 €</span>}
              {!tot.bolloProposto && dg.bollo && <span className="f1-warn-inline">attivo ma non proposto dal motore</span>}
            </div>
          </div>
        </div>
      </div>

      {/* ============ drawer per-riga avanzato ============ */}
      {drawer != null && fatt.righe[drawer] && (() => {
        const i = drawer;
        const r = fatt.righe[i];
        return (
          <div className="f1-overlay right" onMouseDown={(e) => { if (e.target === e.currentTarget) setDrawer(null); }}>
            <div className="f1-drawer" role="dialog" aria-label={'Avanzate riga ' + (i + 1)}>
              <div className="f1-modal-h">
                <span>{'Riga ' + (i + 1) + ' — avanzate'}</span>
                <button type="button" className="f1-x" title="Chiudi" onClick={() => setDrawer(null)}>✕</button>
              </div>
              <div className="f1-modal-b">
                <Campo label="Tipo cessione/prestazione">
                  <select className="f1-in" data-campo={'righe[' + i + '].tipoCessione'} value={r.tipoCessione || ''}
                    onChange={(e) => patchRiga(i, 'tipoCessione', e.target.value)}>
                    {TIPI_CESSIONE.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
                  </select>
                </Campo>
                <label className="f1-check" style={{ margin: '8px 0' }}>
                  <input type="checkbox" data-campo={'righe[' + i + '].ritenuta'} checked={!!r.ritenuta}
                    onChange={(e) => patchRiga(i, 'ritenuta', e.target.checked)} />
                  <span>riga soggetta a ritenuta (richiede il blocco Ritenute)</span>
                </label>
                <div className="f1-sublist-t">Codici articolo</div>
                {(r.codiciArticolo || []).map((ca, j) => (
                  <div className="f1-subrow" key={j}>
                    <input className="f1-in" style={{ flex: 1 }} placeholder="Tipo (es. EAN)" value={ca.tipo || ''}
                      onChange={(e) => patchRiga(i, 'codiciArticolo', r.codiciArticolo.map((x, k) => k === j ? Object.assign({}, x, { tipo: e.target.value }) : x))} />
                    <input className="f1-in" style={{ flex: 2 }} placeholder="Valore" value={ca.valore || ''}
                      onChange={(e) => patchRiga(i, 'codiciArticolo', r.codiciArticolo.map((x, k) => k === j ? Object.assign({}, x, { valore: e.target.value }) : x))} />
                    <button type="button" className="f1-del" onClick={() => patchRiga(i, 'codiciArticolo', r.codiciArticolo.filter((x, k) => k !== j))}>✕</button>
                  </div>
                ))}
                <button type="button" className="f1-btn gray sm" onClick={() => patchRiga(i, 'codiciArticolo', (r.codiciArticolo || []).concat([{ tipo: '', valore: '' }]))}>+ Codice</button>

                <div className="f1-sublist-t">Sconti / maggiorazioni riga</div>
                {(r.sconti || []).map((s, j) => (
                  <div className="f1-subrow" key={j}>
                    <select className="f1-in" style={{ flex: 1 }} value={s.tipo || 'SC'}
                      onChange={(e) => patchRiga(i, 'sconti', r.sconti.map((x, k) => k === j ? Object.assign({}, x, { tipo: e.target.value }) : x))}>
                      <option value="SC">Sconto</option>
                      <option value="MG">Maggiorazione</option>
                    </select>
                    <input className="f1-in num" style={{ flex: 1 }} type="number" step="any" placeholder="%" value={s.percentuale == null ? '' : s.percentuale}
                      onChange={(e) => patchRiga(i, 'sconti', r.sconti.map((x, k) => k === j ? Object.assign({}, x, { percentuale: numOrNull(e.target.value) }) : x))} />
                    <input className="f1-in num" style={{ flex: 1 }} type="number" step="any" placeholder="Importo" value={s.importo == null ? '' : s.importo}
                      onChange={(e) => patchRiga(i, 'sconti', r.sconti.map((x, k) => k === j ? Object.assign({}, x, { importo: numOrNull(e.target.value) }) : x))} />
                    <button type="button" className="f1-del" onClick={() => patchRiga(i, 'sconti', r.sconti.filter((x, k) => k !== j))}>✕</button>
                  </div>
                ))}
                <button type="button" className="f1-btn gray sm" onClick={() => patchRiga(i, 'sconti', (r.sconti || []).concat([{ tipo: 'SC', percentuale: null, importo: null }]))}>+ Sconto/magg.</button>

                <div className="f1-grid" style={{ marginTop: 10 }}>
                  <Campo label="Inizio periodo">
                    <input className="f1-in" type="date" value={r.dataInizioPeriodo || ''}
                      onChange={(e) => patchRiga(i, 'dataInizioPeriodo', e.target.value || null)} />
                  </Campo>
                  <Campo label="Fine periodo">
                    <input className="f1-in" type="date" value={r.dataFinePeriodo || ''}
                      onChange={(e) => patchRiga(i, 'dataFinePeriodo', e.target.value || null)} />
                  </Campo>
                  <Campo label="Rif. amministrazione" wide>
                    <input className="f1-in" maxLength={20} value={r.riferimentoAmministrazione || ''}
                      onChange={(e) => patchRiga(i, 'riferimentoAmministrazione', e.target.value)} />
                  </Campo>
                </div>

                <div className="f1-sublist-t">Altri dati gestionali</div>
                {(r.altriDatiGestionali || []).map((adg, j) => (
                  <div className="f1-subrow" key={j}>
                    <input className="f1-in" style={{ flex: 1 }} placeholder="TipoDato (es. INTENTO)" maxLength={10} value={adg.tipoDato || ''}
                      onChange={(e) => patchRiga(i, 'altriDatiGestionali', r.altriDatiGestionali.map((x, k) => k === j ? Object.assign({}, x, { tipoDato: e.target.value.toUpperCase() }) : x))} />
                    <input className="f1-in" style={{ flex: 2 }} placeholder="Testo" maxLength={60} value={adg.riferimentoTesto || ''}
                      onChange={(e) => patchRiga(i, 'altriDatiGestionali', r.altriDatiGestionali.map((x, k) => k === j ? Object.assign({}, x, { riferimentoTesto: e.target.value }) : x))} />
                    <input className="f1-in num" style={{ flex: 1 }} type="number" step="any" placeholder="Numero" value={adg.riferimentoNumero == null ? '' : adg.riferimentoNumero}
                      onChange={(e) => patchRiga(i, 'altriDatiGestionali', r.altriDatiGestionali.map((x, k) => k === j ? Object.assign({}, x, { riferimentoNumero: numOrNull(e.target.value) }) : x))} />
                    <input className="f1-in" type="date" style={{ flex: 1 }} value={adg.riferimentoData || ''}
                      onChange={(e) => patchRiga(i, 'altriDatiGestionali', r.altriDatiGestionali.map((x, k) => k === j ? Object.assign({}, x, { riferimentoData: e.target.value || null }) : x))} />
                    <button type="button" className="f1-del" onClick={() => patchRiga(i, 'altriDatiGestionali', r.altriDatiGestionali.filter((x, k) => k !== j))}>✕</button>
                  </div>
                ))}
                <button type="button" className="f1-btn gray sm" onClick={() => patchRiga(i, 'altriDatiGestionali', (r.altriDatiGestionali || []).concat([{ tipoDato: '', riferimentoTesto: '', riferimentoNumero: null, riferimentoData: null }]))}>+ Dato gestionale</button>
              </div>
              <div className="f1-modal-f">
                <button type="button" className="f1-btn gray" onClick={() => setDrawer(null)}>Chiudi</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ============ modale anteprima XML ============ */}
      {xml != null && (
        <div className="f1-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setXml(null); }}>
          <div className="f1-modal xml" role="dialog" aria-label="Anteprima XML">
            <div className="f1-modal-h">
              <span>Anteprima XML (FatturaPA)</span>
              <button type="button" className="f1-x" title="Chiudi" onClick={() => setXml(null)}>✕</button>
            </div>
            <div className="f1-modal-b">
              <pre className="f1-xml">{xml}</pre>
            </div>
            <div className="f1-modal-f">
              <button type="button" className="f1-btn gray" onClick={() => setXml(null)}>Chiudi</button>
            </div>
          </div>
        </div>
      )}

      {/* ============ mini-form genera piano rate ============ */}
      {pianoOpen && (
        <PianoRateForm
          win={win}
          nettoDefault={nettoCorrente}
          primaScadenzaDefault={(rateArr[0] && rateArr[0].dataScadenza) || oggiISO()}
          onGenera={applicaPianoRate}
          onClose={() => setPianoOpen(false)}
        />
      )}

      {/* ============ nuovo cliente inline ============ */}
      {cliFormOpen && (
        <ClienteForm
          win={win}
          cliente={null}
          toast={toast}
          onClose={() => setCliFormOpen(false)}
          onSaved={(dto) => {
            setCliFormOpen(false);
            setClienti((prev) => prev.concat([dto]));
            patchTestata('clienteId', dto.id);
            patchTestata('formato', dto.isPa ? 'FPA12' : 'FPR12');
          }}
        />
      )}
    </div>
  );
}
